import { tenantValue } from './tenant.runtime'

/**
 * Base URL for every API call.
 *
 * Resolved from the tenant this session signed in to, falling back to the URL compiled into this
 * build. One build can therefore serve every customer (app.nexys.com) and still behave exactly as
 * before when deployed the old per-tenant way.
 */
export const baseUrl = tenantValue('apiUrl', process.env.REACT_APP_API_URL)

export const API_USER_SIGN_IN = '/auth/login'

export const API_USER_ROLES = '/user/roles'
export const API_USER_PROPS = '/user/props/:userId'
export const API_USER_CREATE = '/user/submit'
export const API_USER_UPDATE = '/user/submit/:userId'
export const API_USER_UPDATE_DATA = '/user/update/:userId'
export const API_USER_PROFILE = '/user/profile'
export const API_USER_WHITE_LABEL = '/user/whiteLabel'
export const API_USER_FILTER = '/user'
export const API_USER_BY_ID = '/user/:id'
export const API_USER_AUTO_COMPLETE = '/user/autoCompleteSearch'
export const API_USER_CHILDREN = '/user/children/:userId'
export const API_USER_UPDATE_STATUS = '/user/updateStatus/:userId'
export const API_USER_UPDATE_CAN_LOGIN = '/user/updateCanLogin/:userId'
export const API_USER_UNSUBSCRIBE = '/api/unsubscribe'
export const API_USER_UPDATE_CHILD_STATUS = '/user/updateChildStatus/:userId'
export const API_USER_DELETE = '/user/delete/:userId'
export const API_USER_RECOVER = '/user/recover/:userId'
export const API_USER_UPDATE_PWD = '/user/updatePwd'
export const API_USER_PERMISSIONS = '/user/permissions'
export const API_USER_GET_LOGS = '/user/logs/:userId/:fieldName'
export const API_USER_GET_ALL_FIELD_LOGS = '/user/logs/field/:fieldName'
export const API_USER_EXPORT_HISTORY = '/user/exportHistory'
export const API_USER_LOGIN_PAGE_MODALS = '/user/loginPageModals'
export const API_USER_LOGIN_SHARING = '/api/loanSharingUsers'
export const API_USER_MULTIPLE = '/user/multiple'
export const API_USER_BULK_UPDATE_STATUS = '/user/bulkUpdateStatus'
export const API_USER_BULK_UPDATE_CAN_LOGIN = '/user/bulkUpdateCanLogin'
export const API_USER_PERMISSION_OVERRIDES = '/user/:userId/permissionOverrides'
export const API_USER_PAGE_PERMISSION_OVERRIDES = '/user/:userId/pagePermissionOverrides'
export const API_USER_EMAIL_TEMPLATES = '/user/:userId/emailTemplates'
export const API_USER_EMAIL_TEMPLATE = '/user/:userId/emailTemplates/:baseTemplateId'
export const API_USER_EMAILS = '/user/emailsByAccountType'

export const API_GET_ALL_PERMISSIONS = '/ac/details'
export const API_SET_PERMISSION_TO_ROLE = '/ac/role/permissions'
export const API_USER_TREE = '/user/userTree'
export const API_AUDIT_LOG = '/auditLog'
export const API_USER_CHILD_BROKER = '/user/childBroker/:userId'
export const API_USER_AUTHORIZED_SIGNORS = '/user/authorizedSignors/:userId'
export const API_USER_WHITE_LABEL_CUSTOM_DNS = '/user/whiteLabelCustomDns/:userId'
export const API_USER_UPDATE_AGREEMENT_TEMPLATE = '/user/updateAgreementTemplate/:userId'
export const API_USER_CONFIGURATION = '/user/profile/configuration/:userId'
export const API_USER_BROKER_VERIFICATION = '/user/brokerVerification'
export const API_USER_BROKER_PAYMENT = '/user/broker/makePayment/:userId'
export const API_USER_SIGN_BROKER_VERIFICATION_PDF = '/user/signBrokerVerificationPdf'
export const API_USER_BROKER_VERIFICATION_BY_USER = '/user/brokerVerification/:userId'
export const API_SEND_BROKER_VERIFICATION_MESSAGE = '/user/brokerVerificationMessage/:userId'
export const API_SEND_BROKER_VERIFICATION_PDF = '/user/sendBrokerVerificationPdf/:userId'
export const API_SEND_SIGN_BROKER_APPROVAL = '/user/sendSignBrokerApproval/:userId'
export const API_USER_DOWNLOAD_BROKER_AGREEMENT_PDF = '/user/downloadBrokerAgreementPdf/:userId'
export const API_USER_COUNTERPARTY_APPROVAL_DOCUMENTS = '/user/counterpartyApprovalDocuments/:userId'
export const API_USER_DOWNLOAD_COUNTERPARTY_APPROVAL_DOCUMENT = '/user/downloadCounterpartyApprovalDocument/:userId'
export const API_USER_COUNTERPARTY_APPROVAL_NOTIFY = '/user/counterpartyApprovalNotify/:userId'
export const API_USER_BY_COMPANY = '/user/companyName'

export const API_USER_ACTIVITY_FILTER = '/user/activity'
export const API_USER_ACTIVITIES_DOWNLOAD_ALL_CSV = '/user/activity/downloadAllCSV'
export const API_USER_ACTIVITIES_ALL = '/user/activity/all'
export const API_USER_ACTIVITIES = '/user/activity/:userId'
export const API_USER_ACTIVITIES_BY_TYPE = '/user/activityByType'
export const API_USER_ACTIVITIES_DOWNLOAD_CSV = `${API_USER_ACTIVITIES}/downloadCSV`
export const API_USER_ACTIVITIES_DOWNLOAD_PDF = `${API_USER_ACTIVITIES}/downloadPDF`

export const API_USER_ADMIN_EMAILS = '/user/getAdminEmails'
export const API_USER_ADMIN_TO_AE_EMAILS = '/user/getAdminToAeEmails'
export const API_USER_FILTER_MASTER_EMAILS = '/user/filterMasterEmails'
export const API_USER_FILTER_ROLES = '/user/filterRoles'
export const API_USER_INTERNAL_EMAILS = '/user/getInternalUserEmails'
export const API_USER_INTERNAL_USERS = '/user/getInternalUsers'
export const API_USER_MENTION_GROUPS = '/user/getMentionGroups'

export const API_HOME_NEWSLETTER = '/home/newsletter'
export const API_HOME_SUBMIT_CONTACTUS = '/home/submitContactUs'
export const API_HOME_CREDIT_BOX = '/home/homepageCreditBox'

export const API_BASE_VALIDATE_ADDRESS = '/home/validateAddress'
export const API_BASE_BANK_ROUTING = '/home/bankRouting/:routingNumber'
export const API_BASE_POSTAL_CODE = '/home/postalCode/:postalCode'

export const API_USER_NOTES = '/user/notes/:userId/:isBorrower'
export const API_USER_NOTES_BY_ID = `${API_USER_NOTES}/:id`

/**
 * Teams
 */
export const API_TEAM_LIST = '/team'
export const API_TEAM_BY_ID = '/team/:id'
export const API_TEAM_CREATE = '/team'
export const API_TEAM_UPDATE = '/team/:id'
export const API_TEAM_DELETE = '/team/:id'
export const API_TEAM_STATS = '/team/stats'
export const API_TEAM_USER_TEAMS = '/team/user/:userId'
export const API_TEAM_EMAIL_TEMPLATES = '/team/:id/emailTemplates'
export const API_TEAM_EMAIL_TEMPLATE = '/team/:id/emailTemplates/:baseTemplateId'

/**
 * User Groups
 */
export const API_USER_GROUP = '/user/group'
export const API_USER_GROUP_BY_ID = '/user/group/:id'

/**
 * Documents
 */
export const API_DOCS_DOWNLOAD = '/documents/download'
export const API_DOCS_MULTI_DOWNLOAD = '/documents/multiDownload'
export const API_DOCS_UPLOAD = '/documents/upload'
export const API_DOCS_COPY = '/documents/copy'

export const API_DOCUMENT_LIST_ALL = '/documents'
export const API_TEMPLATE_PROGRAMS = '/documents/templatePrograms'
export const API_DOCUMENT_LIST = '/documents/:type'
export const API_DOCUMENT_CREATE = '/documents'
export const API_DOCUMENT_UPDATE = '/documents/:id'
export const API_DOCUMENT_DELETE = '/documents/:id'
export const API_DOCUMENT_GET = '/documents/get/:id'
export const API_DOCUMENT_DOWNLOAD_BY_ID = '/documents/download/:id'

/**
 * Loan
 */
export const API_LOAN_OVERVIEW = '/loan/overview'
export const API_LOAN_DOWNLOAD_ALL_CHANGES = '/loan/download/allChanges'
export const API_LOAN_DOWNLOAD_MISMO_XML = '/loan/download/mismoXml'
export const API_LOAN_UPLOAD_MISMO_XML = '/loan/upload/mismoXml'
export const API_LOAN_GET_DUPLICATE_LOANS = '/loan/duplicateLoans/:loanNumber'
export const API_LOAN_UPDATE_FIELDS = '/loan/updateLoanFields'
export const API_LOAN_GET_TIER_LEVEL_LIST = '/loan/getTierLevelList/:programId'
export const API_LOAN_PIPELINE = '/loan/pipeline'
export const API_LOAN_PIPELINE_VIEWS = '/loan/pipelineViews'
export const API_LOAN_PIPELINE_VIEW_BY_ID = '/loan/pipelineViews/:id'
export const API_LOAN_MOVE_TO_SERVICING = '/loan/moveToServicing/:loanNumber'
export const API_LOAN_STATUS_CHANGE = '/loan/statusChange/:status/:loanNumber'
export const API_LOAN_SUB_STATUS_CHANGE = '/loan/subStatusChange/:loanNumber'
export const API_LOAN_SERVICING_STATUS_CHANGE = '/loan/servicingStatusChange/:status/:loanNumber'
export const API_LOAN_LOG = '/loan/log/:key/:propertyId'
export const API_LOAN_DETAIL_LOG = '/loan/loandetail_log'
export const API_LOAN_PROCESS_LOG = '/loan/process_log/:key/:loanNumber'
export const API_LOAN_HISTORY = '/loan/history'
export const API_LOAN_OVERVIEW_SECTION = '/loan/overviewSection'
export const API_LOAN_OVERVIEW_SECTION_BY_LOAN_NUMBER = '/loan/overviewSection/:loanNumber'
export const API_LOAN_OVERVIEW_OVERRIDES = '/loan/overviewOverrides'
export const API_LOAN_TAGS = '/loan/tags/:loanNumber'
export const API_LOAN_TAG_SUGGESTIONS = '/loan/tagSuggestions'
export const API_LOAN_SYSTEM_TAGS = '/loan/systemTags'
export const API_LOAN_ACTIONS = '/loan/actions'
export const API_LOAN_ACTION = '/loan/actions/:actionType/:key'
export const API_LOAN_CUSTOM_INFORMATION = '/loan/customInformation/:menuKey'
export const API_LOAN_UPDATE_CLOSING_DATE = '/loan/updateClosingDate'
export const API_LOAN_UPDATE_LOAN_PROCESS = '/loan/updateLoanProcess'
export const API_LOAN_UPLOAD_WET_SIGNED_TERM_SHEET = '/loan/uploadWetSignedTermSheet'
export const API_LOAN_CLONE_FILE = '/loan/cloneFile'
export const API_LOAN_CLONE_GROUP = '/loan/cloneGroup'
export const API_LOAN_SURVEY = '/loan/survey'
export const API_LOAN_GET_GEOLOCATION = '/loan/geoLocation'
export const API_LOAN_FETCH_FCI_DATA = '/loan/fetchFciData'
export const API_LOAN_BORROWER_OUTSTANDING_ITEMS = '/loan/borrowerOutstandingItems'
export const API_LOAN_CREATE_SAMPLE = '/loan/sampleLoan'
export const API_LOAN_BATCH_IMPORT = '/loan/batchImport'
export const API_LOAN_BATCH_IMPORT_SAMPLE = '/loan/batchImport/sample'
export const API_LOAN_BATCH_IMPORT_HISTORY = '/loan/batchImport/history'
export const API_LOAN_BATCH_IMPORT_REVERT = '/loan/batchImport/:id/revert'
export const API_LOAN_GET_ADDRESS_ROUTES = '/loan/address/routes'
export const API_LOAN_COMPLIANCE = '/loan/compliance'
export const API_LOAN_COMPLIANCE_CONFIG = '/loan/compliance/config'

/**
 * UnderWriting
 */
export const API_LOAN_GET_DATAVERIFY = '/loan/getLoanDataverify/:type'
export const API_LOAN_SUBMIT_DATAVERIFY_SSA_ORDER = '/loan/submitLoanDataverifySSAOrder'
export const API_LOAN_UPDATE_DATAVERIFY_SSA_STATUS = '/loan/updateLoanDataverifySSAStatus/:orderID'
export const API_LOAN_SUBMIT_DATAVERIFY_TAX_ORDER = '/loan/submitLoanDataverifyTaxOrder'
export const API_LOAN_UPDATE_DATAVERIFY_TAX_STATUS = '/loan/updateLoanDataverifyTaxStatus/:orderID'
export const API_LOAN_SUBMIT_DATAVERIFY_LEXIS_ORDER = '/loan/submitLoanLexisOrder'
export const API_LOAN_GET_ALL_DATAVERIFY = '/loan/getAllDataverify/:type'

export const API_DATAVERIFY_CREDENTIAL = '/loan/dataverifyCredential'
export const API_LOAN_DATAVERIFY_CONDITIONS = '/loan/dataverifyCondition'

export const API_LOAN_GET_NY_TRANSACTIONS = 'loan/nyTransactions'
export const API_LOAN_UPDATE_NY_TRANSACTIONS = 'loan/nyTransactions'
export const API_LOAN_GET_NY_TRANSACTIONS_LOG = 'loan/nyTransactions/log/:key'

export const API_LOAN_GET_UNDERWRITING_SUMMARY = 'loan/underwritingSummary'
export const API_LOAN_UPDATE_UNDERWRITING_SUMMARY = 'loan/underwritingSummary'
export const API_LOAN_GET_UNDERWRITING_SUMMARY_LOG = 'loan/underwritingSummary/log/:key'
export const API_LOAN_DOWNLOAD_UNDERWRITING_SUMMARY = 'loan/underwritingSummary/download'

export const API_LOAN_ESCROW_HOLDINGBACK = '/loan/underwriting/escrowHoldingback'

/**
 * Loan Application
 */
export const API_LOAN_APPLICATION_GEO_NAME = '/loan/geoNames/:postalCode'

export const API_ADDITIONAL_PROPERTY_INFO = '/loan/additionalPropertyInfo'
export const API_ADDITIONAL_PROPERTY_INFO_LOGS = '/loan/additionalPropertyInfo/log/:fieldName'
export const API_UPDATE_ADDITIONAL_PROPERTY_INFO = '/loan/additionalPropertyInfo/:id'
export const API_DELETE_ADDITIONAL_PROPERTY_INFO = API_UPDATE_ADDITIONAL_PROPERTY_INFO

export const API_BORROWER_INFO = '/loan/borrowerInfo'
export const API_BORROWER_INFO_AI_EXTRACT = '/loan/borrowerInfo/aiExtract'
export const API_CREDIT_SCORE_AI_EXTRACT = '/loan/creditScore/aiExtract'
export const API_ASSOCIATED_PARTY_UPDATES = '/loan/associatedPartyUpdates'
export const API_TEMPORARY_USER_INFO = '/loan/temporaryUserInfo'
export const API_SWAP_BORROWERS = '/loan/swapBorrowers'
export const API_LOAN_BORROWER_SIGN_STATUS = '/loan/getBorrowerSignAppStatus'
export const API_BORROWER_LOG = '/loan/borrowerLogs/:borrowerSeperator/:fieldName'
export const API_BORROWER_LOG_DOWNLOAD_CSV = `/loan/borrowerLogs/download/CSV`
export const API_BORROWER_LOG_DOWNLOAD_PDF = `/loan/borrowerLogs/download/PDF`
export const API_BORROWER_ACTIVITIES = '/borrowers/activity/:userId'
export const API_ADD_CO_BORROWER = '/loan/addCoBorrower'
export const API_REMOVE_CO_BORROWER = '/loan/removeCoBorrower/:borrowerSeparator'
export const API_BORROWER_VERIFY_TIN = '/loan/verifyBorrowerTin'
export const API_BORROWER_VERIFY_TIN_HISTORY = '/loan/tinVerifyHistory'

export const API_LOAN_APPLICATION_ASSET = 'loan/assetInformation'
export const API_LOAN_APPLICATION_ASSET_CREATE = API_LOAN_APPLICATION_ASSET
export const API_LOAN_APPLICATION_ASSET_RESET = `${API_LOAN_APPLICATION_ASSET}/reset`
export const API_LOAN_APPLICATION_ASSET_LOG = `${API_LOAN_APPLICATION_ASSET}/log`
export const API_LOAN_APPLICATION_ASSET_VERIFY = `${API_LOAN_APPLICATION_ASSET}/verify`
export const API_LOAN_APPLICATION_ASSET_UPDATE = `${API_LOAN_APPLICATION_ASSET}/:id`
export const API_LOAN_APPLICATION_ASSET_DELETE = API_LOAN_APPLICATION_ASSET_UPDATE
export const API_LOAN_APPLICATION_ASSET_VERIFY_BANK_STATEMENT = `${API_LOAN_APPLICATION_ASSET}/:id/verifyBankStatement`

export const API_LOAN_APPLICATION_MAIN_CREDIT_UPDATE = 'loan/assets/mainCredits'
export const API_LOAN_APPLICATION_PURCHASE_CREDIT_UPDATE = 'loan/assets/purchaseCredits'
export const API_LOAN_APPLICATION_ASSET_UPDATE_BY_KEY = 'loan/assets/update/:key'

export const API_LOAN_APPLICATION_LIABILITY = 'loan/liabilityInformation'
export const API_LOAN_APPLICATION_LIABILITY_CREATE = API_LOAN_APPLICATION_LIABILITY
export const API_LOAN_APPLICATION_LIABILITY_RESET = `${API_LOAN_APPLICATION_LIABILITY}/reset`
export const API_LOAN_APPLICATION_LIABILITY_UPDATE = `${API_LOAN_APPLICATION_LIABILITY}/:id`
export const API_LOAN_APPLICATION_LIABILITY_DELETE = API_LOAN_APPLICATION_LIABILITY_UPDATE

export const API_LOAN_APPLICATION_RENTAL_ESTATE = 'loan/rentalEstateInformation'
export const API_LOAN_APPLICATION_RENTAL_ESTATE_BY_ID = 'loan/rentalEstateInformation/:id'
export const API_LOAN_APPLICATION_RENTAL_ESTATE_CLEAR = 'loan/rentalEstateInformation/clear'
export const API_LOAN_APPLICATION_REAL_ESTATE_DOWNLOAD = 'loan/rentalEstateInformation/download'

export const API_LOAN_APPLICATION_TRACK = 'loan/trackRecord'
export const API_LOAN_APPLICATION_TRACK_DOWNLOAD = 'loan/trackRecord/download'
export const API_LOAN_APPLICATION_TRACK_CREATE = API_LOAN_APPLICATION_TRACK
export const API_LOAN_APPLICATION_TRACK_UPDATE = `${API_LOAN_APPLICATION_TRACK}/:id`
export const API_LOAN_APPLICATION_TRACK_DELETE = API_LOAN_APPLICATION_TRACK_UPDATE
export const API_LOAN_APPLICATION_TRACK_CLEAR = 'loan/trackRecord/clear'

export const API_LOAN_APPLICATION_TRACK_DOCUMENT = 'loan/trackRecordDocument'
export const API_LOAN_APPLICATION_TRACK_DOCUMENT_DELETE = `${API_LOAN_APPLICATION_TRACK_DOCUMENT}/:id`

export const API_LOAN_APPLICATION_PULL_CREDIT_REPORT = 'loan/pullCreditReport'
export const API_LOAN_APPLICATION_PULL_CREDIT_REPORT_HISTORY = 'loan/pullCreditReportHistory/:borrowerSeperator'

export const API_LOAN_APPLICATION_SEND_APPLICATION_TO = 'loan/sendApplicationTo'
export const API_LOAN_APPLICATION_SEND_APPLICATION_TO_CO_BORROWERS = 'loan/sendApplicationToCoBorrowers'
export const API_LOAN_APPLICATION_COPY_APPLICATION_TO_URL = 'loan/copyApplicationToUrl'
export const API_LOAN_APPLICATION_SEND_SIGNATURE_REQUEST = 'loan/sendSignatureRequest'
export const API_LOAN_APPLICATION_SEND_SIGNATURE_REMINDER = 'loan/sendSignatureReminder'
export const API_LOAN_APPLICATION_VALIDATE_SIGN_SIGNATURE = 'loan/validateSignSignature'
export const API_LOAN_APPLICATION_UPLOAD_SIGNED_PDF = 'loan/uploadSignedPdf'
export const API_LOAN_APPLICATION_ADD_HAND_SIGNED_PDF = 'loan/addHandSignedPdf'
export const API_LOAN_APPLICATION_SEND_DRAW_REQUEST_TO = 'loan/sendDrawRequestTo'
export const API_LOAN_APPLICATION_HISTORY = 'loan/signApplicaitonHistory'
export const API_LOAN_APPLICATION_ADD_HAND_SIGNED_MLDS_PDF = 'loan/addHandSignedMldsPdf'

export const API_LOAN_APPLICATION_ASSET_TRACK_LOG = '/loan/assetTrack/log/:key'
export const API_LOAN_URL_TO_PDF = '/loan/printLoanDetails'
export const API_LOAN_URL_TO_PDF_BY_FILEKEY = '/loan/printLoanDetailsByFileKey'

export const API_LOAN_APPLICATION_PDF_CHECK_PLACEHOLDER = '/loan/checkLoanAppPlaceholder'
export const API_LOAN_APPLICATION_GET_LOAN_APP_DOCUMENTS = '/loan/getLoanAppDocuments'
export const API_LOAN_APPLICATION_IMPORT = 'loan/importApplication'
export const API_LOAN_APPLICATION_DOWNLOAD = 'loan/downloadApplication'
export const API_LOAN_FIELD_LOCKS = 'loan/fieldLocks'
export const API_LOAN_FIELD_LOCKS_BATCH = 'loan/fieldLocks/batch'
export const API_LOAN_FIELD_LOCKS_ALL = 'loan/fieldLocks/all'
export const API_LOAN_FIELD_LOCKS_HISTORY = 'loan/fieldLocks/history'

/**
 * Structure
 */
export const API_BUSINESS_HOUR = 'home/businessHour'
export const API_LOAN_LOCK_FLOAT_RATE = '/loan/loanLockOrFloatRate'
export const API_LOAN_SAVE_RATESHEET = '/loan/saveLoanRatesheet'
export const API_LOAN_GET_LATEST_RATESHEET = '/loan/getLatestLoanRatesheet'
export const API_LOAN_GET_PROCESS = '/loan/getLoanProcessRate'
export const API_LOAN_EMAIL_RESTRICTIONS = '/loan/emailRestrictions/:loanNumber'
export const API_LOAN_LOCK_EXTENSION = '/loan/lockExtension'
export const API_LOAN_NOTIFY_PDF_TO_CREATOR = '/loan/notifyPdfToCreator'
export const API_LOAN_OVERRIDE_PRICING = '/loan/overridePricing'
export const API_LOAN_REPRICE_AFTER_LOCKED = '/loan/rePriceAfterLocked'
export const API_LOAN_RE_PRICING_INIT = '/loan/rePricingInitData'
export const API_LOAN_SAVE_PROCESS = '/loan/saveLoanProcess'
export const API_LOAN_SAVE_TO_PIPELINE = '/loan/saveToPipeline'
export const API_LOAN_STRUCTURE_HISTORY = '/loan/loanStructureHistory/:index'
export const API_LOAN_STRUCTURE_RELOCK_TIMES = '/loan/getRelockTimes'
export const API_LOAN_SUBMIT_EXCEPTION_REQUEST = '/loan/submitExceptionRequest'
export const API_LOAN_SUBMIT_EXCEPTION_RESPONSE = '/loan/submitExceptionResponse'
export const API_LOAN_CREATE_ADDRESS_DOCUMENT = '/loan/createAddressDocument'
export const API_IMPORT_LOAN_EXCEL = '/loan/importLoanExcel'

/**
 * Loan Submission
 */
export const API_LOAN_GET_SUBMISSION = '/loan/submission'
export const API_LOAN_GET_SUBMISSION_CONDITIONS_TEMPORARY = '/loan/submission/conditionsForTemporaryUser'
export const API_LOAN_SUBMISSION_LOG = '/loan/submission/log/:key'
export const API_LOAN_SUBMISSION_CONDITION_LOGS = '/loan/submission/conditionLogs'
export const API_LOAN_SUBMISSION_CHECKLIST_LOG = '/loan/submission/checklistLog/:no/:key'
export const API_LOAN_SUBMISSION_NOTIFY = '/loan/submission/notify'
export const API_LOAN_VENDOR_LOANS = '/loan/submission/vendor/loans'
export const API_LOAN_UPDATE_SUBMISSION = '/loan/updateSubmission'
export const API_LOAN_BORROWER_UPLOADED_DOCUMENTS = '/loan/borrowerUploadedDocuments'
export const API_LOAN_SUBMISSION_DOCUMENTS = '/loan/getLoanSubmissionDocuments'
export const API_LOAN_SUBMISSION_UPDATED_AT = '/loan/getLoanSubmissionUpdatedAt'
export const API_LOAN_SUBMISSION_REVIEW_REQUEST = '/loan/reviewRequest/:key'
export const API_LOAN_SUBMISSION_DOCUMENT_HISTORY = '/loan/submissionDocumentHistory'
export const API_LOAN_SUBMISSION_CLEARED_UNDERWRITER_TASK = '/loan/conditionClearedUnderwriterTask/:conditionNo'
export const API_LOAN_SUBMISSION_UPLOAD_BORROWER_DOCUMENTS = 'loan/uploadBorrowerDocuments'
export const API_LOAN_SEND_SUBMISSION_PDF_EMAIL = '/loan/sendSubmissionPdfEmail'
export const API_LOAN_PROC_RUSH_PAYMENT = 'loan/process/rushPayment'
export const API_LOAN_SUBMISSION_ORIGINATOR_ADD_NOTES = '/loan/submission/originatorAddNotes'

export const API_LOAN_SUBMIT_TO_LOANSETUP = '/loan/submitToLoanSetup'
export const API_LOAN_SUBMIT_CLOSED_LOAN_REVIEW = '/loan/submitClosedLoanReview'
export const API_LOAN_SUBMIT_CLOSING_CONDITION_CLEARED = '/loan/submitClosingConditionCleared'
export const API_LOAN_SUBMIT_CONDITIONS = '/loan/submitConditions'
export const API_LOAN_SUBMIT_TO_UNDERWRITER = '/loan/submitToUnderwriter'
export const API_LOAN_SUBMIT_TO_AE = '/loan/submitToAE'
export const API_LOAN_SUBMIT_CTC_REVIEW = '/loan/submitCTCReview'
export const API_LOAN_SEND_LOAN_SUBMISSION_DOCUMENT_EMAIL = 'loan/sendLoanSubmissionDocumentEmail'

export const API_LOAN_AI_REVIEW_CONDITION = '/loan/submission/aiReviewCondition/:conditionNo'
// export const API_LOAN_AI_REVIEW_CONDITION_PROMPT = '/loan/submission/aiReviewCondition/:conditionNo/prompt'

/**
 * BytePro
 */
export const API_LOAN_BYTEPRO_LOGS = '/loan/byteproLogs'
export const API_LOAN_BYTEPRO_LOG_DETAIL = '/loan/byteproLog/:id'
export const API_LOAN_GET_BYTEPRO_FILENAME = '/loan/getByteproFileName/:loanNumber'
export const API_LOAN_CONNECT_BYTEPRO = '/loan/connectToBytepro'
export const API_LOAN_GET_LOAN_NUMBER_FROM_BYTEPRO_FILENAME = '/loan/getLoanNumberFromByteproFileName/:loanNumber'

/**
 * Generate Documents
 */
export const API_LOAN_GENERATE_DOCUMENTS = '/loan/generateDocuments'
export const API_LOAN_DELETE_DOCUMENT_HISTORY = '/loan/deleteDocumentHistory'
export const API_LOAN_GENERATE_DOCUMENTS_URL = '/loan/getGenerateDocumentUrl/:id'
export const API_LOAN_GENERATE_DOCUMENTS_LOG = '/loan/generateDocuments/log/:key'
export const API_LOAN_GET_GENERATED_DOCUMENTS = '/loan/getGeneratedDocuments'

/**
 * Document Genie
 */
export const API_LOAN_GET_DOCUMENT_GENIE = '/loan/documentGenie'
export const API_LOAN_SEND_DOCUMENT_GENIE_SIGN_REQUEST = '/loan/documentGenie'
export const API_LOAN_UPLOAD_SIGNED_DOCUMENT_GENIE_PDF = 'loan/uploadSignedDocumentGeniePdf'
export const API_LOAN_GET_OVERVIEW_FOR_DOCUMENT_GENIE = 'loan/overviewForDocumentGenie'

/**
 * Closing Calendar
 */
export const API_CLOSING_CALENDAR = 'loan/closingCalendar'
export const API_CLOSING_CALENDAR_DETAILS = 'loan/closingCalendar/getDetails'
export const API_CLOSING_CALENDAR_SIGN_EMAIL = 'loan/closingCalendar/sendSignEmail'

/**
 * Party
 */
export const API_LOAN_PARTY_OPTIONS = 'loan/party/options/:key'
export const API_LOAN_PARTY = 'loan/party'
export const API_LOAN_PARTY_AI_EXTRACT = 'loan/party/aiExtract'
export const API_LOAN_PARTY_LOG = 'loan/party/log/:loanNumber/:field/:partyName'
export const API_LOAN_PARTY_ADDITIONAL_INFO = 'loan/party/additionalInfo/:type'

export const API_LOAN_PARTY_BY_LOANNUMBER = 'loan/party/:loanNumber'

export const API_LOAN_UPDATE_PARTIES = 'loan/party/:loanNumber/:PartyID'
export const API_LOAN_GET_PARTY_IDS = 'loan/party/ids'
export const API_LOAN_GET_BORROWER_REQUIRED_PARTIES = 'loan/party/borrowerRequired'
export const API_LOAN_DOWNLOAD_PARTY = 'loan/party/download/:loanNumber'
export const API_LOAN_IMPORT_PARTY = 'loan/party/import/:loanNumber'

/**
 * Post Closing
 */
export const API_POST_CLOSING_FIELDS = 'loan/postClosingFields/:menu'
export const API_POST_CLOSING_FIELDS_LOG = 'loan/postClosingFields/log/:menu/:fieldName'
export const API_POST_CLOSING_DOCS = 'loan/postClosingDocs'
export const API_LOAN_SETPOINT = 'loan/postClosing/setPoint'
export const API_LOAN_SETPOINT_PUSH = 'loan/postClosing/setPoint/push'
export const API_LOAN_SETPOINT_DILIGENCE = 'loan/postClosing/setPoint/diligence'
export const API_LOAN_SETPOINT_FACILITIES = 'loan/postClosing/setPoint/facilities'
export const API_LOAN_SETPOINT_DOCUMENTS = 'loan/postClosing/setPoint/documents'
export const API_LOAN_SETPOINT_DOCUMENTS_SEND = 'loan/postClosing/setPoint/documents/send'

export const API_PURCHASE_ADVICE_DATA = '/loan/purchaseAdvice'
export const API_PURCHASE_ADVICE_LOG = '/loan/purchaseAdvice/log/:type/:key'
export const API_PURCHASE_ADVICE_SELLER_DATA = '/loan/purchaseAdviceSeller'
export const API_PURCHASE_ADVICE_SELLER_SIGN = '/loan/purchaseAdviceSeller/sign'
export const API_PURCHASE_ADVICE_SELLER_SIGN_BY_ID = '/loan/purchaseAdviceSeller/sign/:id'
export const API_PURCHASE_ADVICE_SELLER_CANCEL_SIGN_BY_ID = '/loan/purchaseAdviceSeller/cancelSign/:id'
export const API_PURCHASE_ADVICE_SELLER_LOG = '/loan/purchaseAdviceSeller/log/:type/:key'

export const API_LOAN_GET_SHIPPING_INFO = '/loan/shippingInfo'
export const API_LOAN_UPDATE_SHIPPING_INFO = '/loan/shippingInfo'
export const API_LOAN_GET_SHIPPING_INFO_LOG = '/loan/shippingInfo/log/:key'
export const API_SHIPPING_INFO_HEADER = '/admin/config/shippingInfoHeader/:sectionKey'
export const API_SHIPPING_INFO_HEADER_LOG = '/admin/config/shippingInfoHeader/log/:sectionKey'

export const API_LOAN_GET_LOAN_TRADE = '/loan/loanTrade'

//---> Post Closing

export const API_LOAN_DELETE = '/loan/:loanNumber'
export const API_LOAN_DELETE_ALL = '/loan/:loanNumber/all'
export const API_LOAN_RECOVER = '/loan/:loanNumber/recover'

export const API_LOAN_STATISTICS = 'loan/statistics'
export const API_PIPELINE_BUSINESS_DAYS = 'loan/businessDays'

export const API_VENDOR_CREDIT_REPORT = '/vendor'
export const API_VENDOR_OPTIONS_BY_TYPE = '/vendor/:type'
export const API_VENDOR_OPTIONS_BY_TYPE_PUBLIC = '/vendor/:type/public'
export const API_VENDOR_LOGS = '/vendor/:type/logs/:field'
export const API_VENDOR_ENABLED_BY_TYPE = '/vendor/:type/enabled'
export const API_VENDOR_ACTIVE_CREDIT_REPORTS = '/vendor/activeCreditReportVendors'

export const API_CONDITIONS = '/condition'
export const API_CONDITION_BROKER_TEMPLATES = '/condition/brokerTemplates'
export const API_CONDITION_BROKER_TEMPLATE_BY_ID = '/condition/brokerTemplate/:id'
export const API_CONDITIONS_SIMPLY = '/condition/simply'
export const API_CONDITION_CREATE = '/condition'
export const API_CONDITION_UPDATE = '/condition/:id'
export const API_CONDITION_KEY_UPDATE = '/condition/:id/:key'
export const API_CONDITION_DELETE = '/condition/:id'
export const API_CONDITION_DOWNLOAD = '/condition/download'
export const API_CONDITION_IMPORT_PARSE = '/condition/import/parse'
export const API_CONDITION_IMPORT_COMMIT = '/condition/import/commit'
export const API_CONDITION_NEW_NUMBER = '/condition/newNo'
export const API_CONDITIONS_REPLACE_CONTENT = '/condition/replaceContent'
export const API_CONDITION_GET_NEW_NUMBER = '/condition/getNewNo'

export const API_GET_ROLODEX = '/rolodex'
export const API_SUBMIT_ROLODEX = '/rolodex'
export const API_UPDATE_ROLODEX = '/rolodex/:id'
export const API_DELETE_ROLODEX = '/rolodex/:id'
export const API_GET_ALL_ROLODEX = 'rolodex/all'
export const API_GET_ROLODEX_CATEGORIES = '/rolodex/categories'
export const API_IMPORT_ROLODEX = '/rolodex/import'

export const API_TEMPLATE_COMPANY_USERS = '/templateAvailability/users'
export const API_EMAIL_TEMPLATE = '/emailTemplate'
export const API_EMAIL_TEMPLATE_BY_ID = '/emailTemplate/:id'
export const API_ORIGINAL_EMAIL_TEMPLATE = '/originalEmailTemplate'

export const API_EMAIL_CONFIGURATION = '/admin/config/emailConfiguration'

export const API_SERVICING_NOTE_TEMPLATE = '/servicingNoteTemplate/:type'
export const API_SERVICING_NOTE_TEMPLATE_BY_ID = '/servicingNoteTemplate/:id'
export const API_SERVICING_NOTE_TEMPLATE_ORDER_CHANGE = '/servicingNoteTemplateOrderChange/:type'

export const API_PAYMENT_TYPE_TEMPLATE = '/paymentTypeTemplate/:orderType'
export const API_PAYMENT_TYPE_TEMPLATE_BY_ID = `${API_PAYMENT_TYPE_TEMPLATE}/:id`

export const API_INVESTOR_SERVICER = '/investorServicer'
export const API_INVESTOR_SERVICER_GET_SERVICERS = '/investorServicer/getServicers'
export const API_INVESTOR_SERVICER_ITEM = '/investorServicer/:id'

export const API_CONDITIONS_BY_TEMPLATE = '/conditions/:templateNo'
export const API_CONDITIONS_BY_NOS = '/conditions/byConditionNo'

export const API_TEMPLATES = '/template'
export const API_TEMPLATE_CREATE = '/template'
export const API_TEMPLATE_UPDATE = '/template/:id'
export const API_TEMPLATE_DELETE = '/template/:id'
export const API_TEMPLATE_UPDATE_SHOW = '/template/show/:id'

export const API_TASK_TEMPLATE = '/taskTemplate'
export const API_TASK_TEMPLATE_CREATE = '/taskTemplate'
export const API_TASK_TEMPLATE_UPDATE = '/taskTemplate/:id'
export const API_TASK_TEMPLATE_DELETE = '/taskTemplate/:id'
export const API_TASK_TEMPLATE_DOWNLOAD = '/taskTemplate/download'
export const API_TASK_TEMPLATE_IMPORT_PARSE = '/taskTemplate/import/parse'
export const API_TASK_TEMPLATE_IMPORT_COMMIT = '/taskTemplate/import/commit'

export const API_CLOSING_COST_TEMPLATE = '/closingCostTemplate'
export const API_CLOSING_COST_TEMPLATE_BY_ID = '/closingCostTemplate/:id'
export const API_CLOSING_COST_TEMPLATE_BY_PRODUCT_TYPE = '/closingCostTemplate/:productType/:transactionType'

export const API_MORTGAGE_CLAUSE_TEMPLATE = '/mortgageClauseTemplate'
export const API_MORTGAGE_CLAUSE_TEMPLATE_BY_ID = '/mortgageClauseTemplate/:id'

export const API_NOTE_TEMPLATE = '/noteTemplate'
export const API_NOTE_TEMPLATE_BY_ID = '/noteTemplate/:id'
export const API_WIRE_INSTRUCTION_TEMPLATE = '/wireInstructionTemplate'
export const API_WIRE_INSTRUCTION_TEMPLATE_BY_ID = '/wireInstructionTemplate/:id'

export const API_REMINDER_TEMPLATE = '/reminderTemplate'
export const API_REMINDER_TEMPLATE_BY_ID = '/reminderTemplate/:id'
export const API_AI_PROMPT_TEMPLATE = '/aiPromptTemplate'
export const API_AI_PROMPT_TEMPLATE_BY_ID = '/aiPromptTemplate/:id'

export const API_UW_CONDITION_CATEGORY = '/uwConditionCategory'
export const API_UW_CONDITION_CATEGORY_BY_ID = '/uwConditionCategory/:id'
export const API_UW_CONDITION_CATEGORY_CHANGE_ORDER = '/uwConditionCategory/changeOrder'
export const API_UW_CONDITION_CATEGORY_DELETE_CATEGORY = '/uwConditionCategory/deleteCategory'

export const API_BROKER_CONDITION_CATEGORY = '/brokerConditionCategory'
export const API_BROKER_CONDITION_CATEGORY_OPTIONS = '/brokerConditionCategory/options'
export const API_BROKER_CONDITION_CATEGORY_BY_ID = '/brokerConditionCategory/:id'
export const API_BROKER_CONDITION_CATEGORY_CHANGE_ORDER = '/brokerConditionCategory/changeOrder'
export const API_BROKER_CONDITION_CATEGORY_DELETE_CATEGORY = '/brokerConditionCategory/deleteCategory'

export const API_TASK_PROJECT = '/task/project'
export const API_TASK_NEW_TASK = '/task'
export const API_TASK_GET_TASK_BY_PROJECTID = '/task/:projectId'
export const API_TASK_UPDATE_TASK = '/task/:taskId'
export const API_TASK_UPDATE_TASK_STATUS = '/task/status/:taskId'
export const API_TASK_GET_TASK_HISTORY = '/task/history/:taskId'
export const API_TASK_GET_TASK_LOG = '/task/log/:taskId'
export const API_TASK_GET_FIELD_LOG = '/task/log/:taskId/:field'
export const API_TASK_BY_LOAN_NUMBER = '/task/loan/:loanNumber'
export const API_TASK_GET_TASK_BY_TASKNO = '/task/item/:taskNo'
export const API_TASK_ADD_FROM_TEMPLATES = '/task/fromTemplate/:projectId/:loanNumber'
export const API_TASK_BY_USER_ID = '/task/user/:userId'
export const API_TASK_GET_COMMENTS = '/task/comments/:taskId'
export const API_TASK_ADD_COMMENT = '/task/comments/:taskId'
export const API_TASK_EDIT_COMMENT = '/task/comments/:taskId/:commentId'
export const API_TASK_HARDCODED = '/task/hardcodedTaskList'

export const API_CONVERT_TABLE = '/admin/convertTable'
export const API_CONVERT_LOAN = '/admin/convertLoan/:loanNumber'
export const API_ADMIN_GET_SETTING = '/api/setting/:key'
export const API_LOAN_SHARE_SCENARIO = '/api/shareScenario'
export const API_LOAN_SHARE_SCENARIO_GET = '/api/shareScenario/:token/:id'
export const API_LOAN_SCENARIO = '/loan/scenario'

export const API_LOAN_TERM_SHEET = '/api/loanTermSheet/:productType/:transactionType/:type'
export const API_ADMIN_UPDATE_SETTING = '/ac/setting'
export const API_PERMISSION_DETAILS = '/ac/permissionDetails'
export const API_PERMISSION_DESCRIPTION = '/ac/permissionDescription/:id'
export const API_PERMISSION_LOGS = '/ac/permissionLogs/:permission'
export const API_CONFIG_FIELD_LOG = '/admin/config/fieldLog'
export const API_GET_NAVBAR_DATA = '/ac/getNavBarData'

export const API_SEND_BROKER_VERIFICATION_REQUEST = '/user/sendBrokerVerificationRequest/:userId'
export const API_UPDATE_BROKER_VERIFICATION_DOCUMENT = '/user/updateBrokerVerificationRequest/:userId'

export const API_SERVER_LOG = '/admin/serverLog'

/**
 * Order Appraisal
 *
 * All appraisal endpoints live under the `/loan/appraisal/***` namespace:
 *   - `/loan/appraisal/v2/**`     Appraisal2 (current) order CRUD + housekeeping
 *   - `/loan/appraisal/info/**`   legacy single-appraisal-per-loan CRUD
 *   - `/loan/appraisal/vendor/**` vendor-agnostic per-order actions, keyed by IntegrationType
 */
export const API_LOAN_CANCEL_APPRAISAL_INFO_V2 = '/loan/appraisal/v2/cancel'
export const API_LOAN_DELETE_APPRAISAL_INFO_V2 = '/loan/appraisal/v2/:id'
export const API_LOAN_UPDATE_APPRAISAL_INFO_V2 = '/loan/appraisal/v2/:id'
export const API_LOAN_CREATE_APPRAISAL_INFO_V2 = '/loan/appraisal/v2'
export const API_LOAN_CREATE_APPRAISAL_V2_CREATE_LOG = '/loan/appraisal/v2/log'
export const API_LOAN_APPRAISAL_INFORMATION_FEED_V2 = '/loan/appraisal/v2/feed'
export const API_LOAN_APPRAISAL_VENDOR_REFRESH = '/loan/appraisal/v2/refresh'
export const API_LOAN_ORDER_VALUATION_FEASIBILITY_SIGN_REQUEST =
  '/loan/appraisal/order-valuation/:id/feasibility-sign-request'
export const API_LOAN_ORDER_VALUATION = '/loan/appraisal/order-valuation/:id'
export const API_LOAN_APPRAISAL2_STATUS_CHANGE = '/loan/appraisal/v2/status'
export const API_LOAN_APPRAISAL2_MESSAGE_REFRESH = '/loan/appraisal/v2/message-refresh'
export const API_LOAN_APPRAISAL2_DATE_UPDATE = '/loan/appraisal/v2/date'
export const API_LOAN_APPRAISAL2_LOG = '/loan/appraisal/v2/log/:key/:id'
export const API_LOAN_POST_APPRAISAL2_DOCUMENTS = '/loan/appraisal/v2/documents'
export const API_LOAN_POST_APPRAISAL2_PAY_OR_INVOICE = '/loan/appraisal/v2/pay-or-invoice'
export const API_LOAN_GET_APPRAISAL2_PAYMENT_LINK = '/loan/appraisal/v2/payment-link/:id'

export const API_APPRAISAL_INFORMATION = '/loan/appraisal/info'
export const API_APPRAISAL_INFORMATION_UPDATE = '/loan/appraisal/info/:id'
export const API_ALL_ORDER_APPRAISAL2 = '/loan/appraisal/v2'
export const API_STATISTICS_APPRAISAL2 = '/loan/appraisal/v2/statistics'

export const API_LOAN_APPRAISAL_ORDER_DETAILS = '/loan/appraisal/v2/order-details'
// export const API_LOAN_POST_APPRAISAL_ORDER = '/loan/postAppraisalOrder'
export const API_LOAN_SYNC_ORDER_APPRAISAL_INFO = '/loan/appraisal/v2/sync'
export const API_LOAN_GET_APPRAISAL_ORDER_STATUS = '/loan/appraisal/vendor/:type/:appraisalID/status'
export const API_LOAN_GET_APPRAISAL_COMMENTS = '/loan/appraisal/vendor/:type/:appraisalID/comments'
export const API_LOAN_POST_APPRAISAL_COMMENTS = '/loan/appraisal/vendor/:type/comments'
export const API_LOAN_GET_APPRAISAL_REVISION_REASONS = '/loan/appraisal/vendor/:type/revision-reasons'
export const API_LOAN_REQUEST_APPRAISAL_REVISION = '/loan/appraisal/vendor/:type/revision'
// export const API_LOAN_APPRAISAL_PAY_INVOICE = '/loan/appraisalPayInvioce'

export const API_LOAN_GET_APPRAISAL_JOB_ADDONS = '/loan/appraisal/vendor/job-addons'

// Trinity
export const API_LOAN_TRINITY_PROGRESS_STATUS = '/loan/appraisal/trinity/:OrderId/:TrinityOrderId/progress-status'
export const API_LOAN_POST_TRINITY_REPORT_TO_DOCSTORAGE = '/loan/appraisal/trinity/:OrderId/send-to-doc-storage'
export const API_LOAN_GET_TRINITY_COMMENTS_LIENS = '/loan/appraisal/trinity/:OrderId/:TrinityOrderId/comments-liens'
export const API_LOAN_POST_TRINITY_COMMENTS = '/loan/appraisal/trinity/comments'
export const API_LOAN_POST_TRINITY_DOCUMENTS = '/loan/appraisal/trinity/documents'

// Elite
export const API_LOAN_ELITE_PROGRESS_STATUS = '/loan/appraisal/elite/:OrderId/progress-status'
export const API_LOAN_GET_ELITE_VALUATION_COMMENTS = '/loan/appraisal/elite/:OrderId/comments'
export const API_LOAN_POST_ELITE_VALUATION_COMMENTS = '/loan/appraisal/elite/comments'
export const API_LOAN_POST_ELITE_VALUATION_DOCUMENTS = '/loan/appraisal/elite/documents'

// Global Purpose (keyed by internal Appraisal2 id, not IntegrationType)
export const API_LOAN_APPRAISAL_VENDOR_COMMENTS = '/loan/appraisal/v2/:id/vendor-comments'
export const API_LOAN_APPRAISAL_VENDOR_DOCUMENTS = '/loan/appraisal/v2/:id/vendor-documents'
export const API_LOAN_APPRAISAL_VENDOR_IMPORT_DOCUMENTS = '/loan/appraisal/v2/:id/vendor-documents/import'
export const API_LOAN_APPRAISAL_VENDOR_IMPORT_CAPABLE_TYPES = '/loan/appraisal/v2/vendor-import-capable-types'

export const API_LOAN_USER_CHECK_ELITE_VALUATION = '/loan/appraisal/elite/:type/check-user'
export const API_LOAN_USER_GET_ELITE_VALUATION_CREDENTIAL = '/loan/appraisal/elite/:type/credential'
export const API_LOAN_GET_ELITE_VALUATION_DETAILS = '/loan/appraisal/elite/detail'
export const API_LOAN_POST_ELITE_VALUATION = '/loan/appraisal/elite'
export const API_LOAN_GET_BORROWER_ELITE_VALUATION_KEY = '/loan/appraisal/elite/borrower-credential'

export const API_LOAN_GET_APPRAISAL_REPORTS = '/loan/appraisal/vendor/:type/:orderId/reports'

/**
 * Second Marketing
 */
export const API_SECONDARY_MARKETING_SCREEN_DATA = '/loan/secondaryMarketingScreen'
export const API_SECONDARY_MARKETING_AI_EXTRACT = '/loan/secondaryMarketingScreen/aiExtract'

/**
 * Finresi
 */
export const API_LOAN_PRICING = '/loan/priceLoan'
export const API_LOAN_REQUEST_PRICE = '/loan/requestPriceLoan'
export const API_CURRENT_PRODUCT_PROGRAM = '/loan/currentProductTypesPrograms'
export const API_LOCK_EXTENSION_COST = '/loan/lockExtensionCost'
export const API_LOAN_INVESTOR_INFO = '/loan/investorInfo/:ratesheetID/:programID'
export const API_LOAN_PROGRAM_DETAILS = '/loan/loanProgram/:ratesheetID/:productID/:programID/:subProgramID'
export const API_LOAN_RATE_SHEETS = '/loan/ratesheets'

export const API_EXPIRATION_DATES = '/loan/expirationDates'
export const API_EXPIRATION_DATES_UPDATE = '/loan/expirationDates/:type'

export const API_FLOOR_ORDER = '/loan/floodOrder'
export const API_SYNC_NATIONAL_FLOOR = '/loan/syncNationalFlood'
export const API_FIND_NATIONAL_FLOOR = '/loan/findNationalFlood'

export const API_RATESHEET = '/ratesheet/'
export const API_RATESHEET_UPDATE = '/ratesheet/:id'

export const API_RESOURCE_PROGRAM_RESTRICTION = '/resources/programRestriction'
export const API_RESOURCE_LOAN_PROGRAM_BY_PROGRAM_ID = '/resources/loanProgramByProgramID/:programID'
export const API_RESOURCE_RBT_DEF = '/resources/rbtDef'

export const API_GUIDELINE_CONDITION = '/resources/guidelineCondition/:id'

export const API_RESOURCE_COMPANY_APPRAISAL = '/resources/CompanyAppraisal'
export const API_RESOURCE_COMPANY_APPRAISAL_BY_ID = `${API_RESOURCE_COMPANY_APPRAISAL}/:id`

export const API_REVIEW_FORM = '/reviewForm'
export const API_REVIEW_FORM_BY_ID = '/reviewForm/:id'
export const API_REVIEW_FORM_LOG_BY_ID = '/reviewForm/log/:id'
export const API_REVIEW_FORM_APPRAISER_ADD_LOGS = '/reviewForm/:id/appraiserAddLogs'
export const API_REVIEW_FORM_TEMPLATES = '/reviewForm/template'
export const API_REVIEW_FORM_TEMPLATE_BY_ID = '/reviewForm/template/:id'
export const API_REVIEW_FORM_TEMPLATE_ORDER_BY_ID = '/reviewForm/templateOrder/:id'
export const API_REVIEW_FORM_TEMPLATE_VISIBLE_BY_ID = '/reviewForm/templateVisible/:id'
export const API_REVIEW_FORM_TEMPLATE_VISIBLE_LOGIC_BY_ID = '/reviewForm/templateVisibleLogic/:id'
export const API_REVIEW_FORM_AI_AUTOFILL = `${API_REVIEW_FORM_BY_ID}/autofill`
// export const API_REVIEW_FORM_AI_PROMPT = `${API_REVIEW_FORM_BY_ID}/prompt`

export const API_LOAN_APPLICATION_ADDITIONAL_MEMBER = 'loan/additionalMember'
export const API_LOAN_APPLICATION_ADDITIONAL_MEMBER_ITEM = `${API_LOAN_APPLICATION_ADDITIONAL_MEMBER}/:id`
export const API_LOAN_APPLICATION_ADDITIONAL_MEMBER_ASSOCIATED = `${API_LOAN_APPLICATION_ADDITIONAL_MEMBER}/associatedPartyUpdates`
export const API_LOAN_APPLICATION_ADDITIONAL_MEMBER_PULL_CREDIT_REPORT = `${API_LOAN_APPLICATION_ADDITIONAL_MEMBER}/pullCreditReport`
export const API_LOAN_APPLICATION_ADDITIONAL_MEMBER_SEND_SIGNATURE_REQUEST = `${API_LOAN_APPLICATION_ADDITIONAL_MEMBER}/sendSignatureRequest`
export const API_LOAN_APPLICATION_ADDITIONAL_MEMBER_VALIDATE_SIGN_SIGNATURE = `${API_LOAN_APPLICATION_ADDITIONAL_MEMBER}/validateSignSignature`
export const API_LOAN_APPLICATION_ADDITIONAL_MEMBER_UPLOAD_SIGNED_PDF = `${API_LOAN_APPLICATION_ADDITIONAL_MEMBER}/uploadSignedPdf`

export const API_LOAN_PUSH_TO_YIELDEASY = 'loan/pushToYieldeasy'
export const API_LOAN_PUSH_TO_NEXYS = 'loan/pushToNexys'
export const API_LOAN_PUSH_TO_NEXYS_LOCK_STATUS = 'loan/pushToNexys/lockStatus'
export const API_LOAN_PUSH_TO_NEXYS_MERGE_NEW_DATA = 'loan/pushToNexys/mergeNewData'
export const API_LOAN_NEXYS_PEERS = 'loan/nexysPeers'
export const API_LOAN_NEXYS_SHARED_PIPELINE = 'loan/nexysSharedPipeline/:peerId'
export const API_LOAN_NEXYS_SHARED_SHELL = 'loan/nexysShared/:peerId/:loanNumber/shell'
export const API_LOAN_NEXYS_SHARED_PAGE = 'loan/nexysShared/:peerId/:loanNumber/page/:pageKey'
export const API_LOAN_NEXYS_SHARED_DOCUMENT = 'loan/nexysShared/:peerId/:loanNumber/document'

export const API_LOAN_VALIDATE_LOAN_NUMBERS = 'loan/validateLoanNumbers'
export const API_LOAN_GET_ZILLOW_URL = 'loan/getZillowUrl/:zipCode'
export const API_LOAN_SITEXPRO_REPORT = 'loan/sitexProReport'

export const API_LOAN_TRIGGER_ACTION_BUTTON = 'loan/triggerActionButton'

export const API_EMAIL_BROADCAST = '/admin/broadcastEmail'

export const API_BANKER_DATA = '/admin/getBankerData'
export const API_INELIGIBLE_BANKERS = '/admin/getIneligibleBankers'
export const API_BANKER_UPDATE_DATA = '/admin/updateBankerData'
export const API_BANKER_DELETE_DATA = '/admin/deleteBankerData'
export const API_LOGIN_PAGE_MODAL = '/admin/logInPageModal'

export const API_ACCOUNT_EXECUTIVES = '/admin/accountExecutives'
export const API_ACCOUNT_EXECUTIVES_BY_ID = '/admin/accountExecutives/:id'
export const API_PUBLIC_ACCOUNT_EXECUTIVES = '/api/accountExecutives'

export const API_FAQ = '/admin/landingLearnMore'
export const API_FAQ_BY_ID = '/admin/landingLearnMore/:id'
export const API_FAQ_EXCHANGE_ORDER = '/admin/landingLearnMore/order/:id1/:id2'
export const API_PUBLIC_LANDING_LEARN_MORE = '/api/landingLearnMore'
export const API_LOAN_TERM_SHEET_LIST = '/admin/loanTermSheet'
export const API_LOAN_TERM_SHEET_BY_ID = '/admin/loanTermSheet/:id'
export const API_LOAN_TERM_SHEET_ADD_FIELD = '/admin/loanTermSheet/:id/field'
export const API_UPDATE_LOAN_TERM_SHEET = '/admin/loanTermSheet/:id'
export const API_SYNC_LOAN_TERM_SHEET = '/admin/loanTermSheet/sync/:id'
export const API_REMOVE_LOAN_TERM_SHEET = '/admin/loanTermSheet/:id/:index'

/**
 * Loan Budget
 */
export const API_BUDGET_TEMPLATE = 'loan/budget'
export const API_BUDGET_TEMPLATE_BY_ID = 'loan/budget/:id'
export const API_BUDGET_TEMPLATE_ITEM = 'loan/budget/updateData/:id'
export const API_BUDGET_TEMPLATE_TITLE = 'loan/budget/title/:id'
export const API_BUDGET_TEMPLATE_APPLY_TEMPLATE = 'loan/budget/applyTemplate/:id'
export const API_BUDGET_TEMPLATE_BORROWER_CONFIRM = 'loan/budget/borrowerConfirmSubmit/:id'
export const API_BUDGET_TEMPLATE_LOCK_BORROWER = 'loan/budget/lockBorrower/:id'
export const API_BUDGET_TEMPLATE_SEND_TO_BORROWER = 'loan/budget/sendToBorrower/:id'
export const API_BUDGET_TEMPLATE_SITEWIRE = 'loan/budget/:id/sitewire'
export const API_BUDGET_TEMPLATE_SIGN_SIGNATURE = 'loan/budget/validateSignSignature/:id'
export const API_BUDGET_TEMPLATE_PARSE_EXCEL = 'loan/budget/parseExcel'
export const API_BUDGET_TEMPLATE_EXPORT_QA = 'loan/budget/exportQA'
export const API_BUDGET_TEMPLATE_LOGS = 'loan/budget/log/:key'
export const API_BUDGET_TEMPLATE_AI_EXTRACT = 'loan/budget/aiExtract/:id'
export const API_PARSE_FULL_EXCEL = 'loan/budget/parseFullExcel'

/**
 * Construction Lien Waiver e-sign (Draw Request)
 */
export const API_LIEN_WAIVER_ESIGN = 'loan/borrower/drawRequest/lienWaiver'
export const API_LIEN_WAIVER_ESIGN_LIST = 'loan/borrower/drawRequest/lienWaiver/list'
export const API_LIEN_WAIVER_ESIGN_SEND = 'loan/borrower/drawRequest/lienWaiver/send'
export const API_LIEN_WAIVER_ESIGN_VALIDATE = 'loan/borrower/drawRequest/lienWaiver/validate'
export const API_LIEN_WAIVER_ESIGN_SIGN = 'loan/borrower/drawRequest/lienWaiver/sign'
export const API_LIEN_WAIVER_ESIGN_OVERRIDE = 'loan/borrower/drawRequest/lienWaiver/override'

/**
 * Servicing
 */
export const API_LOAN_UPDATE_SERVICE_EXTENSION = '/loan/updateServiceExtension/:id'
export const API_LOAN_CREATE_SERVICE_EXTENSION = 'loan/createServiceExtension'
export const API_LOAN_DELETE_SERVICE_EXTENSION = 'loan/deleteServiceExtension/:id'
export const API_LOAN_GET_SERVICE_EXTENSION = 'loan/getServiceExtension'
export const API_LOAN_GET_ONE_SERVICE_EXTENSION = 'loan/getOneServiceExtension/:id'
export const API_LOAN_EXTENSION_DOCUMENT_DEFAULTS = 'loan/extensionDocumentDefaults'
export const API_LOAN_EXTENSION_DOCUMENT_TEMPLATES = 'loan/extensionDocumentTemplates'
export const API_LOAN_GENERATE_EXTENSION_DOCUMENT = 'loan/generateExtensionDocument/:id'
export const API_LOAN_SEND_EXTENSION_DOCUMENT = 'loan/sendExtensionDocument/:id'
export const API_LOAN_EMAIL_EXTENSION = 'loan/emailExtension/:id'
export const API_LOAN_APPLY_EXTENSION_MATURITY_DATE = 'loan/applyExtensionMaturityDate/:id'
export const API_LOAN_SEND_EXTENSION_FOR_SIGNATURE = 'loan/sendExtensionForSignature/:id'
export const API_LOAN_EXTENSION_SIGNATURE_RECIPIENTS = 'loan/extensionSignatureRecipients/:id'
export const API_LOAN_EXTENSION_SIGNING_PACKAGE = 'loan/extensionSigningPackage/:id'
export const API_LOAN_SIGN_EXTENSION_AGREEMENT = 'loan/signExtensionAgreement/:id'

export const API_LOAN_SERVICING_PARTICIPATION = '/loan/servicingParticipation'
export const API_LOAN_SERVICING_PARTICIPATION_UPDATE = '/loan/servicingParticipation/:id'
export const API_LOAN_SERVICING_PARTICIPATION_GENERATE = '/loan/servicingParticipation/:id/generate'
export const API_LOAN_SERVICING_PARTICIPATION_FUNDING = '/loan/participationFunding'
export const API_LOAN_SERVICING_PARTICIPATION_FUNDING_BY_ID = '/loan/participationFunding/:id'
export const API_LOAN_SERVICING_PARTICIPATION_FUNDING_DEBIT_PREVIEW = '/loan/participationFunding/:id/debit/preview'
export const API_LOAN_SERVICING_PARTICIPATION_FUNDING_DEBIT = '/loan/participationFunding/:id/debit'
export const API_LOAN_SERVICING_PARTICIPATION_FUNDING_DEBIT_REVERSE = '/loan/participationFunding/:id/debit/reverse'
export const API_LOAN_SERVICING_PARTICIPATION_DISTRIBUTIONS = '/loan/participationDistributions'
export const API_LOAN_SERVICING_PARTICIPATION_RESET = '/loan/participationReset'
export const API_LOAN_SERVICING_PARTICIPATION_UPB_RESTORE = '/loan/participationUpbRestore'

export const API_LOAN_DOWNLOAD_SERVICING_DOC2 = '/loan/downloadServicingDocument2'
export const API_LOAN_SEND_SERVICING_DOC_EMAIL = '/loan/sendServicingDocumentEmail'

export const API_LOAN_GET_SERVICING_DOCUMENTS_DATA = '/loan/servicingDocumentData'
export const API_LOAN_PIPELINE_SERVICING_PARTIES = '/loan/pipelineServicingParties'
export const API_LOAN_ALL_SERVICING_DOCUMENTS = '/loan/allServicingDocuments'
export const API_LOAN_ALL_TERM_SHEET_DOCUMENTS = '/loan/allLoanTermSheetDocuments'
export const API_LOAN_MASS_SEND_SERVICING_DOCUMENTS = '/loan/massSendServicingDocuments'

export const API_LOAN_GET_LOAN_INTAKE_DOCUMENTS_DATA = '/loan/loanIntakeDocumentData'
export const API_LOAN_UPDATE_LOAN_INTAKE_DOC_DATA = '/loan/updateLoanIntakeDocumentData'
export const API_LOAN_SEND_LOAN_INTAKE_DOC_EMAIL = '/loan/sendLoanIntakeDocumentEmail'
export const API_LOAN_DOWNLOAD_LOAN_INTAKE_DOC = '/loan/downloadLoanIntakeDocument'
export const API_LOAN_TRID_IMPORTANT_DATES = '/loan/tridImportantDates'

export const API_LOAN_RICHER_VALUES_SUBMIT = 'loan/richerValues/submit'
export const API_LOAN_RICHER_VALUES_FORM = 'loan/richerValues/form/:orderType'
export const API_LOAN_RICHER_VALUES_ORDERS = 'loan/richerValues/orders'
export const API_LOAN_RICHER_VALUES_ORDER_BY_ID = 'loan/richerValues/orders/by-id/:orderId'
export const API_LOAN_RICHER_VALUES_ORDER_DETAILS = 'loan/richerValues/orders/:intakeToken'
export const API_LOAN_RICHER_VALUES_SUBMIT_DRAFT = 'loan/richerValues/orders/:orderId/submit'
export const API_LOAN_RICHER_VALUES_REFERENCE_LOAN_OFFICERS = 'loan/richerValues/reference/loan-officers'
export const API_LOAN_RICHER_VALUES_REFERENCE_ENTITIES = 'loan/richerValues/reference/entities'
export const API_LOAN_RICHER_VALUES_REFERENCE_USERS = 'loan/richerValues/reference/users'
export const API_LOAN_RICHER_VALUES_REFERENCE_REPORT_TYPES = 'loan/richerValues/reference/report-types'
export const API_LOAN_RICHER_VALUES_REFERENCE_INSPECTION_TYPES = 'loan/richerValues/reference/inspection-types'

export const API_LOAN_GET_SERVICING_NOTES = '/loan/servicingNotes'
export const API_LOAN_GET_SERVICING_HOLD = '/loan/servicingHold'
export const API_LOAN_GET_SERVICING_NOTES_LOG = '/loan/servicingNotesLog/:key'
export const API_LOAN_SERVICING_INSURANCE_AI_EXTRACT = '/loan/servicingNotes/insurance/aiExtract'

export const API_LOAN_GET_SERVICING_BORROWING_BASE = '/loan/servicingBorrowingBase'
export const API_LOAN_GET_SERVICING_BORROWING_BASE_LOG = '/loan/servicingBorrowingBaseLog/:key'

export const API_LOAN_GET_SERVICING_DEFAULT_TRACKING = '/loan/servicingDefaultTracking'
export const API_LOAN_GET_SERVICING_DEFAULT_TRACKING_LOG = '/loan/servicingDefaultTrackingLog/:key'

export const API_LOAN_SERVICING_REPORT = '/loan/servicingReport'

// Credit reporting: the payment history furnished to the consumer reporting agencies, and the
// Metro 2 file that carries it.
export const API_CREDIT_REPORTING_CONFIG = '/loan/credit-reporting/config'
export const API_CREDIT_REPORTING_LOAN = '/loan/credit-reporting/loan'
export const API_CREDIT_REPORTING_ENROLL = '/loan/credit-reporting/enroll'
export const API_CREDIT_REPORTING_PREVIEW = '/loan/credit-reporting/preview'
export const API_CREDIT_REPORTING_CYCLE = '/loan/credit-reporting/cycle'
export const API_CREDIT_REPORTING_SNAPSHOT = '/loan/credit-reporting/snapshot'
export const API_CREDIT_REPORTING_VALIDATE = '/loan/credit-reporting/validate'
export const API_CREDIT_REPORTING_RUNS = '/loan/credit-reporting/runs'
export const API_CREDIT_REPORTING_RUN_DOWNLOAD = '/loan/credit-reporting/runs/:id/download'
export const API_CREDIT_REPORTING_RUN_REPORT = '/loan/credit-reporting/runs/:id/report'
export const API_CREDIT_REPORTING_RUN_SUBMIT = '/loan/credit-reporting/runs/:id/submit'
export const API_CREDIT_REPORTING_RUN_VOID = '/loan/credit-reporting/runs/:id/void'

export const API_LOAN_FCI_LOANPORTFOLIO = 'loan/fciPortfolio'
export const API_LOAN_FCI_LOAN_DETAIL = 'loan/fci/loanDetail'
export const API_LOAN_FCI_BORROWER_PAYMENTS = 'loan/fci/borrowerPayments'
export const API_LOAN_FCI_CHARGES = 'loan/fci/charges'
export const API_LOAN_FCI_ATTACHMENTS = 'loan/fci/attachments'
export const API_LOAN_FCI_DOWNLOAD_ATTACHMENTS = 'loan/fci/downloadAttachment'
export const API_LOAN_FCI_FUNDING_HISTORY = 'loan/fci/fundingHistory'
export const API_LOAN_FCI_ONE_TIME_PAYMENT_LINK = 'loan/fci/oneTimePaymentLink'

// Trust Ledger
export const API_TRUST_LEDGER_VALIDATE_SIGN_SIGNATURE = 'loan/trustLedgerSignUrlValitate'
export const API_TRUST_LEDGER_STATUS_CHANGE_DRAW_AGREEMENT = 'loan/trustLedgerAgreementStatusChange'
export const API_TRUST_LEDGER = 'loan/trustLedger'
export const API_TRUST_LEDGER_ALL = 'loan/trustLedgerAll'
export const API_TRUST_LEDGER_CREDIT_LINE = 'loan/trustLedger/creditLine'
export const API_TRUST_ACCOUNT_ASSIGNMENT = 'loan/trustAccountAssignment'
export const API_TRUST_LEDGER_DRAW_TYPE = 'loan/trustLedgerDrawType'
export const API_TRUST_LEDGER_ADDITIONAL = 'loan/trustLedgerAdditional'
export const API_TRUST_LEDGER_ADDITIONAL_INFO = 'loan/trustLedgerAdditionalInfo'
export const API_TRUST_LEDGER_NOTIFICATION = 'loan/trustLedgerNotification'

export const API_CONSTRUCTION_LEDGER = 'loan/constructionLedger'

export const API_LOAN_TRUST_LEDGER_ORDER_HISTORY = 'loan/trustLedgerOrderHistory/:id'
export const API_LOAN_TRUST_LEDGER_DRAW_ISSUED_TASK = 'loan/trustLedgerDrawIssuedTask'
export const API_LOAN_TRUST_LEDGER_INTEREST = 'loan/trustLedgerInterest'
export const API_LOAN_TRUST_LEDGER_INTEREST_CREATE = API_LOAN_TRUST_LEDGER_INTEREST
export const API_LOAN_TRUST_LEDGER_INTEREST_RESET = `${API_LOAN_TRUST_LEDGER_INTEREST}/reset`
export const API_LOAN_TRUST_LEDGER_INTEREST_TRANSFER = `${API_LOAN_TRUST_LEDGER_INTEREST}/transfer`
export const API_LOAN_TRUST_LEDGER_INTEREST_UPDATE = `${API_LOAN_TRUST_LEDGER_INTEREST}/:id`
export const API_LOAN_TRUST_LEDGER_INTEREST_DELETE = API_LOAN_TRUST_LEDGER_INTEREST_UPDATE

// Note
export const API_NOTE = '/loan/note/:loanNumber'
export const API_NOTE_BY_ID = '/loan/note/:id'
export const API_NOTE_READ_BY_ID = '/loan/note/read/:id'
export const API_NOTE_PINNED = '/loan/note/pinned/:loanNumber'
export const API_NOTE_PIN_BY_ID = '/loan/note/pin/:id'

// Invoice
export const API_LOAN_INVOICE = '/loan/invoice'
export const API_LOAN_INVOICE_UPDATE = '/loan/invoice/:id'
export const API_LOAN_INVOICE_DOWNLOAD = '/loan/invoice/downloadCsv'
export const API_LOAN_INVOICE_LOG = '/loan/invoice/log/:id/:key'
export const API_LOAN_INVOICE_AI_EXTRACT = '/loan/invoice/aiExtract'

// Loan Charges
export const API_LOAN_CHARGE = '/loan/charge'
export const API_LOAN_CHARGE_UPDATE = '/loan/charge/:id'
export const API_LOAN_CHARGE_SUMMARY = '/loan/chargeSummary'
/** Waive a charge (PUT) or reverse the waiver (DELETE). */
export const API_LOAN_CHARGE_WAIVE = '/loan/charge/:id/waive'
export const API_LOAN_CHARGE_CATEGORY = '/loan/chargeCategory'
export const API_LOAN_CHARGE_CATEGORY_USED = '/loan/chargeCategory/used'
export const API_LOAN_CHARGE_CATEGORY_UPDATE = '/loan/chargeCategory/:id'
export const API_LOAN_CHARGE_SPLIT_PAYEES = '/loan/chargeSplitPayees'
export const API_LOAN_CHARGE_LENDERS = '/loan/chargeLenders'

// Rate Indexes (adjustable-rate loans)
export const API_RATE_INDEX = '/loan/rateIndex'
export const API_RATE_INDEX_UPDATE = '/loan/rateIndex/:key'
export const API_RATE_INDEX_VALUES = '/loan/rateIndex/:key/values'
export const API_RATE_INDEX_VALUE_DELETE = '/loan/rateIndex/:key/values/:id'
export const API_RATE_INDEX_SYNC = '/loan/rateIndex/sync'
export const API_LOAN_ARM_PREVIEW = '/loan/servicing/arm'
export const API_LOAN_ARM_RUN = '/loan/servicing/arm/run'
export const API_LOAN_ARM_RUN_ALL = '/loan/servicing/arm/run-all'
export const API_LOAN_ARM_PORTFOLIO = '/loan/servicing/arm/portfolio'
export const API_LOAN_ARM_RUNS = '/loan/servicing/arm/runs'
export const API_LOAN_TERM_CHANGE_NOTICE = '/loan/termChanges/:id/notice'
export const API_LOAN_TERM_CHANGE_NOTICE_EMAIL = '/loan/termChanges/:id/notice/email'

// Rental Ledger
export const API_REO_RENTAL_LEDGER = '/loan/reo/rentalLedger'
export const API_REO_DATA = '/loan/reo/data'
export const API_REO_HISTORY = '/loan/reo/log'

export const API_REO_CONTACT = '/loan/reo/contact'
export const API_REO_CONTACT_BY_ID = '/loan/reo/contact/:id'
export const API_REO_DOCUMENT_DATA = '/loan/reo/documentData'
export const API_REO_ALL_DOCUMENTS = '/loan/reo/allDocuments'
export const API_REO_DOWNLOAD_DOCUMENT = '/loan/reo/downloadDocument'
export const API_REO_SEND_DOCUMENT_EMAIL = '/loan/reo/sendDocumentEmail'

// Loan Contacts
export const API_LOAN_CONTACT = '/loan/contact'
export const API_LOAN_CONTACT_UPDATE = '/loan/contact/:id'

export const API_LOAN_GENERATE_PAYOFF = '/loan/generatePayoff'
export const API_LOAN_PAYOFF_LINE_ITEMS = '/loan/getPayoffLineItems'
export const API_LOAN_COMBINED_PAYOFF_CANDIDATES = '/loan/combinedPayoffCandidates'
export const API_LOAN_COMBINED_PAYOFF_LINE_ITEMS = '/loan/getCombinedPayoffLineItems'
export const API_LOAN_GENERATE_COMBINED_PAYOFF = '/loan/generateCombinedPayoff'
export const API_LOAN_PAYOFF_STATEMENTS = '/loan/payoffStatements'
export const API_LOAN_PAYOFF_STATEMENT_PDF = '/loan/payoffStatements/:id/pdf'
export const API_LOAN_PAYOFF_STATEMENT_APPROVE = '/loan/payoffStatements/:id/approve'
export const API_LOAN_PAYOFF_STATEMENT = '/loan/payoffStatements/:id'
export const API_LOAN_PAYOFF_STATEMENT_RECORD_RELEASE = '/loan/payoffStatements/:id/recordRelease'
export const API_LOAN_COLLATERAL = '/loan/collateral'
export const API_LOAN_COLLATERAL_ITEM = '/loan/collateral/:id'
export const API_LOAN_GENERATE_REINSTATEMENT = '/loan/generateReinstatement'
export const API_LOAN_REINSTATEMENT_LINE_ITEMS = '/loan/getReinstatementLineItems'

/**
 * Loan Funding Disbursement
 */
export const API_LOAN_AMORTIZATION_SCHEDULE = '/loan/amortizationSchedule'
export const API_LOAN_AMORTIZATION_SCHEDULE_ADDITIONAL_PRINCIPAL_REDUCTIONS =
  '/loan/amortizationSchedule/additionalPrincipalReductions'
export const API_LOAN_AMORTIZATION_SCHEDULE_SETTINGS = '/loan/amortizationSchedule/settings'
export const API_LOAN_FUNDING_DISBURSEMENT = '/loan/fundingDisbursement'
export const API_LOAN_FUNDING_DISBURSEMENT_LOG = '/loan/fundingDisbursementLog/:key'
export const API_LOAN_AMORTIZATION_SCHEDULE_DOWNLOAD = '/loan/amortizationSchedule/downloadPdf'
export const API_LOAN_AMORTIZATION_SCHEDULE_EXCEL_DOWNLOAD = '/loan/amortizationSchedule/downloadExcel'

/**
 * Loan Contractor
 */
export const API_LOAN_CONTRACTOR = '/loan/contractor'
export const API_LOAN_CONTRACTOR_BY_NO = '/loan/contractor/:no'
export const API_LOAN_CONTRACTOR_PAYMENTS = '/loan/contractor/:no/payments'
export const API_LOAN_CONTRACTOR_DOCUMENTS = '/loan/contractor/:no/documents'
export const API_LOAN_CONTRACTOR_LOG = '/loan/contractor/:no/log/:key'
export const API_LOAN_CONTRACTOR_SEND_APPLICATION = '/loan/contractor/:no/sendApplication'
export const API_LOAN_CONTRACTOR_VALIDATE_SIGN = '/loan/contractor/:no/signValidate'
export const API_LOAN_CONTRACTOR_SIGN = '/loan/contractor/:no/signApplication'
export const API_LOAN_CONTRACTOR_SIGN_CONSENT = '/loan/contractor/:no/signConsentForm'
export const API_LOAN_CONTRACTOR_APP_PDF = '/loan/contractor/:no/getPDF'
export const API_LOAN_CONTRACTOR_CONSENT_PDF = '/loan/contractor/:no/getConsentPDF'
export const API_LOAN_CONTRACTOR_UPLOAD_DOCUMENTS = '/loan/contractor/:no/uploadDocuments'
export const API_LOAN_CONTRACTOR_MESSAGE = '/loan/contractor/:no/message'

export const API_PDF_SSA = '/pdf/exportSSAPdf/:borrowerSeperator'
export const API_PDF_LOAN_SIGN = '/pdf/exportLoanSignAppPdf'
export const API_PDF_LOAN_STRUCTURE = '/pdf/loanStructurePdf'
export const API_PDF_DOCUMENT_GENIE = '/pdf/exportDocumentGenie'
export const API_PDF_APPRAISAL_REVIEW = '/pdf/appraisalReviewPdf'
export const API_PDF_PURCHASE_ADVICE = '/pdf/purchaseAdvicePdf'
export const API_PDF_LOAN_TERM_SHEET = '/pdf/loanTermSheetPdf'
export const API_PDF_CREDIT_AUTHORIZATION = '/pdf/exportCreditAuthorizationFormPdf'
export const API_PDF_LOAN_SUBMISSION = '/pdf/exportLoanSubmissionPdf'
export const API_PDF_POST_CLOSING = 'loan/exportPostClosingPdf/:menu'
export const API_PDF_CLOSING_CALENDAR = '/pdf/closingCalendarPdf'

export const API_STRIPE_CLIENT_SESSION_KEY = '/payment/stripeClientKey'
export const API_PAYMENT_CREATE = '/payment/create'
export const API_PAYMENT_CONFIRM = '/payment/confirm'
export const API_PAYMENT_USIO_CHARGE = '/payment/usio/charge'
export const API_PAYMENT_USIO_CONV_FEE = '/payment/usio/convenienceFee'
export const API_PAYMENT_ACTIVE_PROVIDER = '/payment/activeProvider'
export const API_USIO_PORTAL_STATUS = '/payment/usio/portal/status'
export const API_USIO_PORTAL_BALANCE = '/payment/usio/portal/balance'
export const API_USIO_PORTAL_TRANSACTIONS = '/payment/usio/portal/transactions'
export const API_USIO_PORTAL_ACH_BATCHES = '/payment/usio/portal/achBatches'
export const API_USIO_PORTAL_ACH_RETURNS = '/payment/usio/portal/achReturns'
export const API_USIO_PORTAL_RISK_REPORT = '/payment/usio/portal/riskReport'
export const API_USIO_PORTAL_STATEMENT = '/payment/usio/portal/statement'
export const API_STRIPE_INTENT = '/payment/stripeIntent'
export const API_STRIPE_INTENT_BY_ID = '/payment/stripeIntent/:id'
export const API_PAYMENT_DATA = '/payment/paymentData'
export const API_PAYMENT_FIND_DATA = '/payment/findStripeIntent'

/**
 * Loan Investor
 */

export const API_LOAN_INVESTOR = '/loan/investor'
export const API_LOAN_INVESTOR_UPDATE = '/loan/investor/:id'

/**
 * Tax Forms
 */

export const API_LOAN_DOWNLOAD_TAX_FORMS = '/pdf/1098-forms'

/**
 * Payments
 */
export const API_PAY_PLAID_CLIENT_TOKEN = '/payment/plaid/clientToken/:type'
export const API_PAY_PLAID_ACCOUNTS_BALANCE = '/payment/plaid/balance'
export const API_PAY_SEND_PAYMENT_LINK = '/payment/stripe/sendPaymentLink'
export const API_PAY_INVOICE = '/payment/invoice'
export const API_REFUND_INVOICE = '/payment/refundInvoice/:intentId'
export const API_PAY_INVOICE_ALLOCATION = '/payment/invoice/:intentId/allocation'
export const API_PAY_INVOICE_STATUS_LABEL = '/payment/invoice/:intentId/statusLabel'

/**
 * HUD Screen
 */
export const API_LOAN_GET_HUD1_PAGE1 = '/loan/hud1/:page'
export const API_LOAN_HUD1_AI_EXTRACT_FEES = '/loan/hud1/aiExtractFees'
export const API_LOAN_GET_LOG_HUD1_PAGE1 = '/loan/hud1/log/:key'
export const API_LOAN_GET_HUD1_NOTES = '/loan/hud1/notes/:key'
export const API_LOAN_DOWNLOAD_HUD1_PDF = '/loan/hud1/downloadPDF/:page'
export const API_LOAN_DOWNLOAD_HUD1_TEMPLATE_PDF = '/loan/hud1/downloadTemplatePDF/:page'
export const API_LOAN_DOWNLOAD_NET_FUNDING_SHEET = '/loan/hud1/downloadNetFundingSheet'
export const API_LOAN_SEND_NET_FUNDING_SHEET_EMAIL = '/loan/hud1/sendNetFundingSheetEmail'
export const API_LOAN_DOWNLOAD_FEE_WORKSHEET = '/loan/hud1/downloadFeeWorksheet'
export const API_LOAN_DOWNLOAD_LOAN_ESTIMATE_PAGE2 = '/loan/hud1/downloadLoanEstimatePage2'
export const API_LOAN_DOWNLOAD_LOAN_ESTIMATE_PAGE3 = '/loan/hud1/downloadLoanEstimatePage3'
export const API_LOAN_DOWNLOAD_CLOSING_DISCLOSURE_PAGE1 = '/loan/hud1/downloadClosingDisclosurePage1'
export const API_LOAN_DOWNLOAD_CLOSING_DISCLOSURE_PAGE2 = '/loan/hud1/downloadClosingDisclosurePage2'
export const API_LOAN_DOWNLOAD_CLOSING_DISCLOSURE_PAGE3 = '/loan/hud1/downloadClosingDisclosurePage3'
export const API_LOAN_DOWNLOAD_CLOSING_DISCLOSURE_PAGE4 = '/loan/hud1/downloadClosingDisclosurePage4'
export const API_LOAN_DOWNLOAD_CLOSING_DISCLOSURE_PAGE5 = '/loan/hud1/downloadClosingDisclosurePage5'
export const API_LOAN_DOWNLOAD_TRANSMITTAL_SUMMARY_1008 = '/loan/hud1/downloadTransmittalSummary1008'
export const API_LOAN_DOWNLOAD_DISBURSEMENT_ITEMS = '/loan/hud1/disbursementItems'
export const API_LOAN_CLOSING_COST_ESTIMATE_HUD1 = '/loan/hud1/closingCostEstimate'
export const API_LOAN_CHANGE_FEE_WORKSHEET_TEMPLATE = '/loan/hud1/changeTemplate/:templateNo'
export const API_LOAN_DOWNLOAD_MLDS = '/loan/mldsPdf'
export const API_LOAN_DOWNLOAD_CLOSING_INFORMATION = '/loan/closingInformationPdf'
export const API_LOAN_SIGN_MLDS = '/loan/signMldsPdf'
export const API_LOAN_SEND_SIGN_MLDS_REQUEST = '/loan/sendMldsSignatureRequest'
export const API_LOAN_DOWNLOAD_AGGREGATE_ESCROW = '/loan/hud1/downloadAggregateEscrow'
export const API_LOAN_DOWNLOAD_CREDIT_DENIAL = '/loan/creditDenialPdf'
export const API_LOAN_ADD_CREDIT_DENIAL = '/loan/addCreditDenialPdf'

/**
 * Admin
 */
export const API_ADMIN_ACCOUNTING_CATEGORY = '/admin/accounting/category'
export const API_ADMIN_ACCOUNTING_CATEGORY_BY_ID = '/admin/accounting/category/:id'

export const API_ADMIN_ACCOUNTING_VENDOR_CATEGORY = '/admin/accounting/vendorCategory'
export const API_ADMIN_ACCOUNTING_VENDOR_CATEGORY_BY_ID = '/admin/accounting/vendorCategory/:id'

export const API_ADMIN_ACCOUNTING_TRUST_ACCOUNT = '/admin/accounting/trustAccount'
export const API_ADMIN_ACCOUNTING_TRUST_ACCOUNT_BY_ID = '/admin/accounting/trustAccount/:id'
export const API_ADMIN_ACCOUNTING_TRUST_ACCOUNT_OPTIONS = '/admin/accounting/trustAccount/options'
export const API_ADMIN_ACCOUNTING_TRUST_ACCOUNT_LEDGER = '/admin/accounting/trustAccount/:id/ledger'
export const API_ADMIN_ACCOUNTING_TRUST_ACCOUNT_BACKFILL = '/admin/accounting/trustAccount/backfill'
export const API_ADMIN_ACCOUNTING_TRUST_ACCOUNT_TRANSFER = '/admin/accounting/trustAccount/transfer'
export const API_ADMIN_ACCOUNTING_BANK_DIRECTORY = '/admin/accounting/bankDirectory'
export const API_ADMIN_ACCOUNTING_BANK_DIRECTORY_IMPORT = '/admin/accounting/bankDirectory/import'
export const API_ADMIN_ACCOUNTING_TRUST_ACCOUNT_ENTRY = '/admin/accounting/trustAccountEntry'
export const API_ADMIN_ACCOUNTING_TRUST_ACCOUNT_ENTRY_BY_ID = '/admin/accounting/trustAccountEntry/:id'
export const API_ADMIN_ACCOUNTING_TRUST_LEDGER_PARTY = '/admin/accounting/trustLedgerParty/:partyType/:partyId'
export const API_ADMIN_ACCOUNTING_VENDOR = '/admin/accounting/vendor'
export const API_ADMIN_ACCOUNTING_VENDOR_BY_ID = '/admin/accounting/vendor/:id'
export const API_ADMIN_ACCOUNTING_VENDOR_DOWNLOAD = '/admin/accounting/vendor/downloadCsv'
export const API_ADMIN_ACCOUNTING_VENDOR_PAYMENTS = '/admin/accounting/vendor/:id/payments'

export const API_ADMIN_ACCOUNTING_RECEIVABLE = '/admin/accounting/receivable'
export const API_ADMIN_ACCOUNTING_RECEIVABLE_BY_ID = '/admin/accounting/receivable/:id'
export const API_ADMIN_ACCOUNTING_RECEIVABLE_PDF = '/admin/accounting/receivable/:id/pdf'
export const API_ADMIN_ACCOUNTING_RECEIVABLE_XLSX = '/admin/accounting/receivable/:id/xlsx'
export const API_ADMIN_ACCOUNTING_RECEIVABLE_REMIND = '/admin/accounting/receivable/:id/remind'
export const API_ADMIN_ACCOUNTING_RECEIVABLE_PAID_EMAIL = '/admin/accounting/receivable/:id/paid-email'
export const API_ADMIN_ACCOUNTING_RECEIVABLE_SETTINGS = '/admin/accounting/receivable/settings'

export const API_ADMIN_ACCOUNTING = 'admin/accounting/data'
export const API_ADMIN_ACCOUNTING_BY_ID = 'admin/accounting/data/:id'
export const API_ADMIN_ACCOUNTING_HISTORY_BY_ID = 'admin/accounting/data/:id/history'
export const API_ADMIN_ACCOUNTING_RELATIONS_BY_ID = 'admin/accounting/data/:id/relations'
export const API_ADMIN_ACCOUNTING_BY_FILE_ID = 'admin/accounting/data/:fileId/file'

export const API_ADMIN_ACCOUNTING_ACCOUNT = 'admin/accounting/account'
export const API_ADMIN_ACCOUNTING_ACCOUNT_CLEAR = 'admin/accounting/account/clear'
export const API_ADMIN_ACCOUNTING_ACCOUNT_BY_ID = 'admin/accounting/account/:id'
export const API_ADMIN_ACCOUNTING_ACCOUNT_DOWNLOAD = 'admin/accounting/account/download'
export const API_ADMIN_ACCOUNTING_ACCOUNT_IMPORT = 'admin/accounting/account/import'
export const API_ADMIN_ACCOUNTING_ACCOUNT_IMPORT_QUICKBOOKS = 'admin/accounting/account/import-quickbooks'
export const API_ADMIN_ACCOUNTING_ACCOUNT_TRANSACTIONS = 'admin/accounting/account/:accountNo/transactions'
export const API_ADMIN_ACCOUNTING_ACCOUNT_TRANSACTIONS_BY_ID = 'admin/accounting/account/:accountNo/transactions/:id'
export const API_ADMIN_ACCOUNTING_ACCOUNT_TRANSACTIONS_SPLIT =
  'admin/accounting/account/:accountNo/transactions/:id/split'
export const API_ADMIN_ACCOUNTING_ACCOUNT_TRANSACTIONS_RECONCILE =
  'admin/accounting/account/:accountNo/transactions/reconcile'
export const API_ADMIN_ACCOUNTING_ACCOUNT_TRANSACTIONS_RECONCILIATION_HISTORY =
  'admin/accounting/account/:accountNo/transactions/reconciliation-history'
export const API_ADMIN_ACCOUNTING_ACCOUNT_TRANSACTIONS_RECONCILIATION_DETAIL =
  'admin/accounting/account/:accountNo/transactions/reconciliation-history/:reconciliationId'
export const API_ADMIN_ACCOUNTING_ACCOUNT_TRANSACTIONS_BEGINNING_BALANCE =
  'admin/accounting/account/:accountNo/transactions/beginning-balance'
export const API_ADMIN_ACCOUNTING_ACCOUNT_TRANSACTIONS_IMPORT =
  'admin/accounting/account/:accountNo/transactions/import'
export const API_ADMIN_ACCOUNTING_GENERAL_LEDGER = 'admin/accounting/general-ledger'
export const API_ADMIN_ACCOUNTING_GENERAL_LEDGER_IMPORT = 'admin/accounting/general-ledger/import'
export const API_ADMIN_ACCOUNTING_GENERAL_LEDGER_DOWNLOAD = 'admin/accounting/general-ledger/download'
export const API_ADMIN_ACCOUNTING_GENERAL_LEDGER_NEXT_REF = 'admin/accounting/general-ledger/next-ref-no'
export const API_ADMIN_ACCOUNTING_GENERAL_LEDGER_ENTRY = 'admin/accounting/general-ledger/entry/:id'
export const API_ADMIN_ACCOUNTING_TRIAL_BALANCE = 'admin/accounting/general-ledger/trial-balance'
export const API_ADMIN_ACCOUNTING_FINANCIAL_STATEMENTS = 'admin/accounting/general-ledger/financial-statements'
export const API_ADMIN_ACCOUNTING_WAREHOUSE_LINES = 'admin/accounting/warehouse-lines'
export const API_ADMIN_ACCOUNTING_WAREHOUSE_LINES_BY_ID = 'admin/accounting/warehouse-lines/:id'
export const API_ADMIN_ACCOUNTING_WAREHOUSE_LINES_OPTIONS = 'admin/accounting/warehouse-lines/options'
export const API_ADMIN_ACCOUNTING_LOAN_SUBLEDGER = 'admin/accounting/loan-subledger'
export const API_ADMIN_ACCOUNTING_LOAN_SUBLEDGER_BY_ID = 'admin/accounting/loan-subledger/:id'
export const API_ADMIN_ACCOUNTING_TREASURY = 'admin/accounting/treasury'
export const API_ADMIN_ACCOUNTING_TREASURY_BANK_ACCOUNTS = 'admin/accounting/treasury/bank-accounts'
export const API_ADMIN_ACCOUNTING_TREASURY_BANK_ACCOUNTS_BY_ID = 'admin/accounting/treasury/bank-accounts/:id'
export const API_ADMIN_ACCOUNTING_TREASURY_WIRE_BLOTTER = 'admin/accounting/treasury/wire-blotter'
export const API_ADMIN_ACCOUNTING_TREASURY_WIRE_BLOTTER_BY_ID = 'admin/accounting/treasury/wire-blotter/:id'
export const API_ADMIN_ACCOUNTING_LOAN_SALES_TRADES = 'admin/accounting/loan-sales-trades'
export const API_ADMIN_ACCOUNTING_LOAN_SALES_TRADES_BY_ID = 'admin/accounting/loan-sales-trades/:id'
export const API_ADMIN_ACCOUNTING_LOAN_REPURCHASES = 'admin/accounting/loan-repurchases'
export const API_ADMIN_ACCOUNTING_LOAN_REPURCHASES_BY_ID = 'admin/accounting/loan-repurchases/:id'
export const API_ADMIN_ACCOUNTING_REPURCHASE_RESERVE = 'admin/accounting/repurchase-reserve'
export const API_ADMIN_ACCOUNTING_HEDGING_FAIR_VALUE = 'admin/accounting/hedging-fair-value'
export const API_ADMIN_ACCOUNTING_HEDGING_FAIR_VALUE_BY_ID = 'admin/accounting/hedging-fair-value/:id'
export const API_ADMIN_ACCOUNTING_ESCROW_CUSTODIAL = 'admin/accounting/escrow-custodial'
export const API_ADMIN_ACCOUNTING_ESCROW_CUSTODIAL_SETTINGS = 'admin/accounting/escrow-custodial/settings'
export const API_ADMIN_ACCOUNTING_SERVICING_MSR = 'admin/accounting/servicing-msr'
export const API_ADMIN_ACCOUNTING_SERVICING_MSR_LOANS = 'admin/accounting/servicing-msr/loans'
export const API_ADMIN_ACCOUNTING_SERVICING_MSR_LOANS_BY_ID = 'admin/accounting/servicing-msr/loans/:id'
export const API_ADMIN_ACCOUNTING_SERVICING_MSR_SETTINGS = 'admin/accounting/servicing-msr/settings'
export const API_ADMIN_ACCOUNTING_DASHBOARD = 'admin/accounting/dashboard'
export const API_ADMIN_ACCOUNTING_RECONCILIATION_ENGINE = 'admin/accounting/reconciliation-engine'

export const API_ADMIN_ACCOUNTING_FILE = 'admin/accounting/file'
export const API_ADMIN_ACCOUNTING_FILE_BY_ID = 'admin/accounting/file/:id'

export const API_ADMIN_ALTERNATIVE_NAMES = 'admin/alternativeNames'

export const API_ADMIN_CONFIG_BY_TYPE = 'admin/config/:type'

/**
 * Reports
 */
export const API_REPORTS = '/reports/all'
export const API_REPORTS_PLACEHOLDERS = '/reports/placeholders'
export const API_REPORTS_LOAN_DETAILS_FIELD_VALUES = '/reports/loanDetailsFieldValues'
export const API_REPORTS_PREVIEW = '/reports/preview'
export const API_REPORTS_DOWNLOAD = '/reports/download'
export const API_REPORTS_CSV_DOWNLOAD = '/reports/csvdownload'
export const API_REPORTS_PIPE_DOWNLOAD = '/reports/pipedownload'
export const API_REPORTS_HMDA_PIPE_JOBS = '/reports/hmda/jobs'
export const API_REPORTS_HMDA_PIPE_JOB = '/reports/hmda/jobs/:jobId'
export const API_REPORTS_HMDA_PIPE_JOB_CANCEL = '/reports/hmda/jobs/:jobId/cancel'
export const API_REPORTS_HMDA_PIPE_JOB_DOWNLOAD = '/reports/hmda/jobs/:jobId/download'
export const API_REPORTS_SERVICING = '/reports/servicing'
export const API_REPORTS_SERVICING_OPTIONS = '/reports/servicing-options'

// Background servicing-report runs: start detached, poll, cancel, download when ready.
export const API_REPORTS_SERVICING_JOBS = '/reports/servicing/jobs'
export const API_REPORTS_SERVICING_JOB = '/reports/servicing/jobs/:jobId'
export const API_REPORTS_SERVICING_JOB_CANCEL = '/reports/servicing/jobs/:jobId/cancel'
export const API_REPORTS_SERVICING_JOB_DOWNLOAD = '/reports/servicing/jobs/:jobId/download'
// NMLS Mortgage Call Report: the FV6 upload file and a preview of what it would carry.
export const API_REPORTS_MCR_XML = '/reports/mortgage-call/xml'
export const API_REPORTS_MCR_XML_PREVIEW = '/reports/mortgage-call/xml/preview'
export const API_REPORTS_LOAN_DISCOUNT = '/reports/loan-discounts/:loanNumber'
export const API_REPORTS_LATE_NOTICES_CONFIG = '/reports/late-notices/config'
export const API_REPORTS_LATE_NOTICES_PREVIEW = '/reports/late-notices/preview'
export const API_REPORTS_LATE_NOTICES_GENERATE = '/reports/late-notices/generate'
export const API_REPORTS_LATE_NOTICES_STATUS = '/reports/late-notices/status/:jobId'
export const API_REPORTS_LATE_NOTICES_CANCEL = '/reports/late-notices/cancel/:jobId'
export const API_REPORTS_LATE_NOTICES_DOWNLOAD = '/reports/late-notices/download/:jobId'
export const API_REPORTS_LATE_NOTICES_SCHEDULE = '/reports/late-notices/schedule'
export const API_REPORTS_LATE_NOTICES_SCHEDULE_RUN = '/reports/late-notices/schedule/run'
export const API_REPORTS_LATE_NOTICES_TEMPLATES = '/reports/late-notices/templates'
export const API_REPORTS_LATE_NOTICES_TEMPLATE_PREVIEW = '/reports/late-notices/template-preview'
export const API_REPORTS_INSURANCE_NOTICES_CONFIG = '/reports/insurance-notices/config'
export const API_REPORTS_INSURANCE_NOTICES_GENERATE = '/reports/insurance-notices/generate'
export const API_REPORTS_INSURANCE_NOTICES_STATUS = '/reports/insurance-notices/status/:jobId'
export const API_REPORTS_INSURANCE_NOTICES_CANCEL = '/reports/insurance-notices/cancel/:jobId'
export const API_REPORTS_INSURANCE_NOTICES_DOWNLOAD = '/reports/insurance-notices/download/:jobId'
export const API_REPORTS_INSURANCE_NOTICES_SCHEDULE = '/reports/insurance-notices/schedule'
export const API_REPORTS_INSURANCE_NOTICES_SCHEDULE_RUN = '/reports/insurance-notices/schedule/run'
export const API_REPORTS_INSURANCE_NOTICES_TEMPLATES = '/reports/insurance-notices/templates'
export const API_REPORTS_INSURANCE_NOTICES_TEMPLATE_PREVIEW = '/reports/insurance-notices/template-preview'
export const API_REPORTS_BALLOON_NOTICES_CONFIG = '/reports/balloon-notices/config'
export const API_REPORTS_BALLOON_NOTICES_GENERATE = '/reports/balloon-notices/generate'
export const API_REPORTS_BALLOON_NOTICES_STATUS = '/reports/balloon-notices/status/:jobId'
export const API_REPORTS_BALLOON_NOTICES_CANCEL = '/reports/balloon-notices/cancel/:jobId'
export const API_REPORTS_BALLOON_NOTICES_DOWNLOAD = '/reports/balloon-notices/download/:jobId'
export const API_REPORTS_BALLOON_NOTICES_SCHEDULE = '/reports/balloon-notices/schedule'
export const API_REPORTS_BALLOON_NOTICES_SCHEDULE_RUN = '/reports/balloon-notices/schedule/run'
export const API_REPORTS_BALLOON_NOTICES_TEMPLATES = '/reports/balloon-notices/templates'
export const API_REPORTS_BALLOON_NOTICES_TEMPLATE_PREVIEW = '/reports/balloon-notices/template-preview'
export const API_REPORTS_RATE_NOTICES_PREVIEW = '/reports/rate-notices/preview'
export const API_REPORTS_RATE_NOTICES_GENERATE = '/reports/rate-notices/generate'
export const API_REPORTS_RATE_NOTICES_STATUS = '/reports/rate-notices/status/:jobId'
export const API_REPORTS_RATE_NOTICES_CANCEL = '/reports/rate-notices/cancel/:jobId'
export const API_REPORTS_RATE_NOTICES_DOWNLOAD = '/reports/rate-notices/download/:jobId'
export const API_REPORTS_BY_ID = '/reports/:id'

/**
 * Closing Costs
 */
export const API_CLOSING_COST_SETUPS = '/closingCostSetup'
export const API_CLOSING_COST_SETUP_CLOSING_CALENDAR = '/closingCostSetup/closing-calendar'
export const API_GET_CLOSING_COST_SETUP = '/closingCostSetup/:id'
export const API_CLOSING_COST_SETUP_UPDATE = '/closingCostSetup/:id'
export const API_LOAN_GET_ELECTIVE_CLOSING_COSTS = '/loan/hud1/getElectiveClosingCosts'
export const API_LOAN_ADD_CLOSING_COSTS = '/loan/hud1/addClosingCosts'
export const API_LOAN_DELETE_CLOSING_COST = '/loan/hud1/deleteClosingCost/:id'

export const API_WAREHOUSE_LENDER = '/warehouseLender'
export const API_WAREHOUSE_LENDER_BY_ID = '/warehouseLender/:id'

export const API_ADMIN_CREDENTIALS = '/admin/config/credentials'
export const API_ADMIN_CREDENTIAL_TYPES = '/admin/config/credentialTypes'

/**
 * Loan Payment
 */
export const API_LOAN_PAYMENT = 'loan/loanPayment'
export const API_LOAN_PAYMENT_BY_ID = 'loan/loanPayment/:id'
export const API_LOAN_PAYMENT_FILL = 'loan/loanPayment/getLoanPaymentFillData'
export const API_LOAN_PAYMENT_DISTRIBUTION_PREVIEW = 'loan/loanPayment/distributionPreview'
export const API_LOAN_PAYMENT_PAYOFF_CHARGES = 'loan/loanPayment/payoffCharges'
export const API_LOAN_PAYMENT_LATE_CHARGE_CALCULATION = 'loan/loanPayment/getLateChargeCalculation'
export const API_LOAN_PAYMENT_DOWNLOAD = 'loan/loanPayment/download/:format'
export const API_LOAN_PAYMENT_LOG = '/loan/loanPayment/log/:id/:key'
export const API_LOAN_PAYMENT_PHONE = 'loan/loanPayment/phone'
export const API_LOAN_PAYMENT_PHONE_AVAILABILITY = 'loan/loanPayment/phone/availability'
export const API_LOAN_PAYMENT_PHONE_QUOTE = 'loan/loanPayment/phone/quote'
export const API_LOAN_PAYMENT_PHONE_DEFAULTS = 'loan/loanPayment/phone/defaults'
export const API_LOAN_NSF_CONFIG = 'loan/nsf/config'
export const API_LOAN_NSF_SEND_NOTICE = 'loan/nsf/sendNotice/:reversalId'
export const API_LOAN_NSF_GENERATE_NOTICE = 'loan/nsf/generateNotice/:reversalId'
export const API_LOAN_NSF_NOTICE = 'loan/nsf/notice/:reversalId'

/**
 * Reminder
 */

export const API_REMINDER = '/reminder'
export const API_REMINDER_BY_ID = '/reminder/:id'
export const API_REMINDER_FIND_ALL = '/reminder/listAll'
export const API_REMINDER_LOG = '/reminder/log/:no/:key'

/**
 * Ticket
 */
export const API_GET_TICKETS_FILTER = '/ticket'
export const API_GET_ALL_MESSAGES_FILTER = '/ticket/msg/:ticketId/:stackNum/'
export const API_ADD_TICKET_MESSAGE = '/ticket/msg'
export const API_TICKET_UPDATE_STATUS = '/ticket/status/:ticketId'
export const API_TICKET_UPDATE_FILE = '/ticket/file/:ticketId'
export const API_DELETE_INTERNAL_TICKET = '/ticket/internal/:id'
export const API_DELETE_EXTERNAL_TICKET = '/ticket/external/:id'
export const API_GET_TICKET_CONFIG = '/ticket/config'
export const API_GET_TICKET_DETAILS = '/ticket/details/:ticketId'

/**
 * Lightning Docs
 */
export const API_LIGHTNING_DOCS_RECORD = 'loan/lightningDocs/records'
export const API_LIGHTNING_DOCS_RECORD_BY_ID = 'loan/lightningDocs/record/:recordId'
export const API_LIGHTNING_DOCS_CHECK_APP_STATUS = `${API_LIGHTNING_DOCS_RECORD_BY_ID}/status`

export const API_AUTOMATE_LOAN_DOCS_PACKAGES = 'loan/automateLoanDocs/packages'
export const API_AUTOMATE_LOAN_DOCS_RECORDS = 'loan/automateLoanDocs/records'
export const API_AUTOMATE_LOAN_DOCS_GENERATE = 'loan/automateLoanDocs/generate'
export const API_AUTOMATE_LOAN_DOCS_RECORD_BY_ID = 'loan/automateLoanDocs/records/:id'

export const API_GO_DOCS_RECORDS = 'loan/goDocs/records'
export const API_GO_DOCS_RECORD_BY_ID = 'loan/goDocs/records/:id'

/**
 * DocMagic
 */
export const API_DOCMAGIC_GENERATE = 'loan/docmagic/generate'
export const API_DOCMAGIC_PACKAGES = 'loan/docmagic/packages'
export const API_DOCMAGIC_HISTORY = 'loan/docmagic/history'
export const API_DOCMAGIC_HISTORY_BY_ID = 'loan/docmagic/history/:id'

/**
 * MLDS
 */
export const API_LOAN_MLDS = 'loan/mlds'

/**
 * Application Email Templates
 */
export const API_APPLICATION_EMAIL_TEMPLATE = '/applicationEmailTemplate'
export const API_APPLICATION_EMAIL_TEMPLATE_BY_ID = '/applicationEmailTemplate/:id'
export const API_APPLICATION_EMAIL_TEMPLATE_DUPLICATE = '/applicationEmailTemplate/duplicate/:id'
export const API_APPLICATION_EMAIL_TEMPLATE_ENABLE = '/applicationEmailTemplate/enable/:id'
export const API_APPLICATION_EMAIL_TEMPLATE_VISIBLE_LOGIC_BY_ID = '/applicationEmailTemplate/visibleLogic/:id'

export const API_LOAN_GET_ACH = '/loan/ach'
export const API_LOAN_GET_ACH_LOG = '/loan/achLog/:key'

// Portfolio-wide servicing pages (top-header ACH / Statements)
export const API_SERVICING_ACH_LIST = '/loan/servicing-ach/list'
export const API_SERVICING_ACH_LIST_EXCEL = '/loan/servicing-ach/list/excel'
export const API_LOAN_GRID_CONFIG = '/loan/grid-config/:gridKey'
export const API_SERVICING_ACH_NACHA = '/loan/servicing-ach/nacha'
export const API_SERVICING_ACH_CONFIG = '/loan/servicing-ach/config'
export const API_SERVICING_ACH_PROCESS = '/loan/servicing-ach/process'
export const API_SERVICING_ACH_PROCESS_STATUS = '/loan/servicing-ach/process/status/:jobId'
export const API_SERVICING_ACH_RUNS = '/loan/servicing-ach/runs'
export const API_SERVICING_ACH_RUN_DOWNLOAD = '/loan/servicing-ach/runs/download'
export const API_SERVICING_ACH_PROVIDER = '/loan/servicing-ach/provider'
export const API_SERVICING_ACH_RETURNS = '/loan/servicing-ach/returns'
export const API_SERVICING_ACH_RETURNS_SWEEP = '/loan/servicing-ach/returns/sweep'
export const API_SERVICING_ACH_RETURNS_APPLY = '/loan/servicing-ach/returns/apply'
export const API_SERVICING_ACH_RETURNS_IGNORE = '/loan/servicing-ach/returns/ignore'
export const API_SERVICING_ACH_RUN_SUBMIT_PROVIDER = '/loan/servicing-ach/runs/submitProvider'
export const API_SERVICING_ACH_RUN_REPORT = '/loan/servicing-ach/runs/report'
export const API_SERVICING_ACH_RUN_EXCEL = '/loan/servicing-ach/runs/excel'
export const API_SERVICING_ACH_RUN_REVERSE = '/loan/servicing-ach/runs/reverse'
export const API_SERVICING_ACH_RUN_DELETE = '/loan/servicing-ach/runs/:id'
export const API_SERVICING_ACH_RUN_REPORT_DATA = '/loan/servicing-ach/runs/:id/report'
// Participant payouts: the check queue, paying it, and reprints.
// Paying parties on a loan: borrower refunds, contractor and vendor payments.
// Servicing workflow: the thresholds the nightly scan works to, and an on-demand run of it.
export const API_SERVICING_WORKFLOW_CONFIG = '/loan/servicing/workflow/config'
export const API_SERVICING_WORKFLOW_RUN = '/loan/servicing/workflow/run'

// First / second payment defaults: the collections priority list, and the per-loan read.
export const API_SERVICING_PAYMENT_DEFAULTS = '/loan/payment-defaults'
export const API_SERVICING_PAYMENT_DEFAULTS_LOAN = '/loan/payment-defaults/loan'
export const API_SERVICING_PAYMENT_DEFAULTS_SCAN = '/loan/payment-defaults/scan'
// Active defaults on a loan: payment (past the Penalties grace), maturity, insurance.
export const API_LOAN_SERVICING_DEFAULTS = '/loan/servicing-defaults'
export const API_LOAN_SERVICING_DEFAULTS_BULK = '/loan/servicing-defaults/bulk'
export const API_LOAN_VOUCHERS = '/loan/vouchers'
export const API_LOAN_VOUCHER_BY_ID = '/loan/vouchers/:id'
export const API_LOAN_VOUCHER_PROJECTION = '/loan/vouchers/projection'
export const API_LOAN_ESCROW_ANALYSIS = '/loan/escrow-analysis'
export const API_LOAN_ESCROW_ANALYSIS_PREVIEW = '/loan/escrow-analysis/preview'
export const API_LOAN_ESCROW_ANALYSIS_DEFAULTS = '/loan/escrow-analysis/defaults'
export const API_LOAN_ESCROW_ANALYSIS_STATEMENT = '/loan/escrow-analysis/:id/statement'
export const API_LOAN_ESCROW_ANALYSIS_STATEMENTS = '/loan/escrow-analysis/statements'
export const API_LOAN_ESCROW_ANALYSIS_APPLY = '/loan/escrow-analysis/:id/apply'

// Debit Requests: collecting money IN by ACH debit, as the counterpart to the payout side -
// a borrower's downpayment / cash to close, or an investor's funds for a loan or a draw.
// `queue`, `preview` and `draft` work across loans; the rest read the `loan-number` header.
export const API_LOAN_DEBIT_REQUESTS = '/loan/debitRequests'
export const API_LOAN_DEBIT_REQUEST = '/loan/debitRequests/:id'
export const API_LOAN_DEBIT_REQUEST_CANCEL = '/loan/debitRequests/:id/cancel'
export const API_LOAN_DEBIT_REQUEST_SETTLE = '/loan/debitRequests/:id/settle'
export const API_LOAN_DEBIT_REQUEST_RE_REQUEST = '/loan/debitRequests/:id/reRequest'
export const API_LOAN_DEBIT_REQUEST_QUEUE = '/loan/debitRequests/queue'
export const API_LOAN_DEBIT_REQUEST_PREVIEW = '/loan/debitRequests/preview'
export const API_LOAN_DEBIT_REQUEST_DRAFT = '/loan/debitRequests/draft'

// Rate & Payment Changes (dated changes to a loan's rate, P&I and escrow)
export const API_LOAN_TERM_CHANGES = '/loan/termChanges'
export const API_LOAN_TERM_CHANGE = '/loan/termChanges/:id'
export const API_LOAN_PAYABLES = '/loan/payables'
export const API_LOAN_PAYABLE_BY_ID = '/loan/payables/:id'
export const API_LOAN_PAYABLE_PAYEES = '/loan/payables/payees'
export const API_LOAN_PAYABLE_SUGGESTIONS = '/loan/payables/suggestions'
export const API_PAYMENT_VOUCHERS = '/loan/payment-vouchers'
export const API_PAYMENT_VOUCHER_BY_ID = '/loan/payment-vouchers/:id'
export const API_PAYMENT_VOUCHER_APPROVE = '/loan/payment-vouchers/:id/approve'
export const API_PAYMENT_VOUCHER_REJECT = '/loan/payment-vouchers/:id/reject'
export const API_PAYMENT_VOUCHER_CONSTRUCTION = '/loan/payment-vouchers/construction'
export const API_PAYMENT_VOUCHER_CONSTRUCTION_SUGGESTIONS = '/loan/payment-vouchers/construction/suggestions'
export const API_PAYMENT_VOUCHER_REPORT = '/loan/payment-vouchers/report/:format'
export const API_PAYMENT_CHECK_QUEUE = '/loan/payment-checks/queue'
export const API_PAYMENT_CHECK_QUEUE_EXCEL = '/loan/payment-checks/queue/excel'
// Taking draws across loans. The loan number is in the path, not the `loan-number` header: the
// screen moves between loans within one page.
export const API_DRAW_ADVISORY = '/loan/drawAdvisory/:loanNumber'
export const API_GLOBAL_DRAW = '/loan/globalDraw/:loanNumber'
export const API_GLOBAL_DRAW_PAYEES = '/loan/globalDraw/:loanNumber/payees'
export const API_GLOBAL_DRAW_PARTICIPANTS = '/loan/globalDraw/:loanNumber/participants'
export const API_PAYMENT_CHECK_PAY = '/loan/payment-checks/pay'
export const API_PAYMENT_CHECK_HOLD = '/loan/payment-checks/hold'
export const API_PAYMENT_CHECK_RUNS = '/loan/payment-checks/runs'
export const API_PAYMENT_CHECK_REMITTANCE = '/loan/payment-checks/remittance'
export const API_PAYMENT_CHECK_RUN_REPORT = '/loan/payment-checks/runs/report'
export const API_PAYMENT_CHECK_RUN_EXCEL = '/loan/payment-checks/runs/excel'
export const API_PAYMENT_CHECK_RUN_REPORT_DATA = '/loan/payment-checks/runs/:id/report'
export const API_PAYMENT_CHECK_RUN_DATE = '/loan/payment-checks/runs/date'
export const API_PAYMENT_CHECK_RUN_REVERSE = '/loan/payment-checks/runs/reverse'
export const API_PAYMENT_CHECK_RUN_DELETE = '/loan/payment-checks/runs/:id'
export const API_PAYMENT_CHECK_RUN_DOWNLOAD = '/loan/payment-checks/runs/download'
export const API_PAYMENT_CHECK_RUN_PRINT = '/loan/payment-checks/runs/print'
export const API_PAYMENT_CHECK_PROVIDER = '/loan/payment-checks/provider'
export const API_PAYMENT_CHECK_RUN_SUBMIT_PROVIDER = '/loan/payment-checks/runs/submitProvider'
export const API_PAYMENT_CHECK_REPRINT = '/loan/payment-checks/reprint'
export const API_PAYMENT_CHECK_RETURNED = '/loan/payment-checks/returned'
export const API_PAYMENT_CHECK_RETURNED_UNDO = '/loan/payment-checks/returned/:payableId'
export const API_PAYMENT_CHECK_DEPOSIT_NOTICES = '/loan/payment-checks/runs/:id/deposit-notices'
export const API_PAYMENT_CHECK_DEPOSIT_NOTICES_SEND = '/loan/payment-checks/runs/:id/deposit-notices/send'
export const API_FUND_PAYOUT_DEPOSIT_NOTICES = '/fund/payout/:payoutRunId/deposit-notices'
export const API_FUND_PAYOUT_DEPOSIT_NOTICES_SEND = '/fund/payout/:payoutRunId/deposit-notices/send'
export const API_ACH_RUN_DEBIT_NOTICES = '/loan/servicing-ach/runs/:id/debit-notices'
export const API_ACH_RUN_DEBIT_NOTICES_SEND = '/loan/servicing-ach/runs/:id/debit-notices/send'
export const API_DEPOSIT_NOTICE_CONFIG = '/loan/deposit-notices/config'
export const API_SERVICING_ACH_SCHEDULE = '/loan/servicing-ach/schedule'
export const API_SERVICING_ACH_SCHEDULE_RUN = '/loan/servicing-ach/schedule/run'
export const API_SERVICING_STATEMENTS = '/loan/servicing-statements'
export const API_SERVICING_STATEMENTS_GENERATE = '/loan/servicing-statements/generate'
export const API_SERVICING_STATEMENTS_GENERATE_STATUS = '/loan/servicing-statements/generate/status/:jobId'
export const API_SERVICING_STATEMENTS_GENERATE_CANCEL = '/loan/servicing-statements/generate/cancel/:jobId'
export const API_SERVICING_STATEMENT_SCHEDULE = '/loan/servicing-statements/schedule'
export const API_SERVICING_STATEMENT_SCHEDULE_RUN = '/loan/servicing-statements/schedule/run'
// Several schedules, one per statement type and date. `schedules` is a distinct path from the
// single-schedule `schedule` above, which is kept for the backend-ahead-of-SPA deploy window.
export const API_SERVICING_STATEMENT_SCHEDULES = '/loan/servicing-statements/schedules'
export const API_SERVICING_STATEMENT_SCHEDULE_BY_ID = '/loan/servicing-statements/schedules/:id'
export const API_SERVICING_STATEMENT_SCHEDULE_BY_ID_RUN = '/loan/servicing-statements/schedules/:id/run'
export const API_SERVICING_STATEMENT_EMAIL = '/loan/servicing-statements/email'
export const API_SERVICING_STATEMENT_DOWNLOAD = '/loan/servicing-statements/download'
export const API_SERVICING_STATEMENT_GENERATE_ONE = '/loan/servicing-statement/generate/:loanNumber'
export const API_SERVICING_STATEMENTS_LOAN = '/loan/servicing-statements/loan/:loanNumber'
export const API_SERVICING_STATEMENT_BATCHES = '/loan/servicing-statements/batches'

// Servicing > Tax Forms: the portfolio-wide IRS Form 1098 run.
export const API_SERVICING_TAX_FORM_CANDIDATES = '/loan/servicing-tax-forms/candidates'
export const API_SERVICING_TAX_FORMS_GENERATE = '/loan/servicing-tax-forms/generate'
export const API_SERVICING_TAX_FORMS_GENERATE_STATUS = '/loan/servicing-tax-forms/generate/status/:jobId'
export const API_SERVICING_TAX_FORMS_GENERATE_CANCEL = '/loan/servicing-tax-forms/generate/cancel/:jobId'
export const API_SERVICING_TAX_FORM_BATCHES = '/loan/servicing-tax-forms/batches'
export const API_SERVICING_TAX_FORM_BATCH = '/loan/servicing-tax-forms/batches/:id'
export const API_SERVICING_TAX_FORM_BATCH_EMAIL = '/loan/servicing-tax-forms/batches/email'
export const API_SERVICING_TAX_FORM_EMAIL = '/loan/servicing-tax-forms/email'
export const API_SERVICING_TAX_FORM_DOWNLOAD = '/loan/servicing-tax-forms/download'
export const API_SERVICING_TAX_FORMS_LOAN = '/loan/servicing-tax-forms/loan/:loanNumber'
export const API_SERVICING_TAX_FORM = '/loan/servicing-tax-forms/form/:id'
export const API_SERVICING_STATEMENT_BATCH = '/loan/servicing-statements/batches/:id'
// Tax filing: the filer registry, the review area and the transmissions. Mounted at /tax rather
// than under /loan because two of the six returns are not loan-scoped at all.
export const API_TAX_META = '/tax/meta'
export const API_TAX_FILERS = '/tax/filers'
export const API_TAX_FILER_OWNERS = '/tax/filers/owners'
export const API_TAX_FILER = '/tax/filers/:id'
export const API_TAX_FILER_DEACTIVATE = '/tax/filers/:id/deactivate'
export const API_TAX_FILER_RESOLVE_LOAN = '/tax/filers/resolve/loan/:loanNumber'
// Producing the forms: the eligibility list, the run, and what it produced. The 1098 has its own
// run under /loan/servicing/taxForm, because its figures are decided per loan.
export const API_TAX_PRODUCE_CANDIDATES = '/tax/produce/candidates'
export const API_TAX_PRODUCE_START = '/tax/produce/start'
export const API_TAX_PRODUCE_STATUS = '/tax/produce/status/:jobId'
export const API_TAX_PRODUCE_CANCEL = '/tax/produce/cancel/:jobId'
export const API_TAX_PRODUCE_BATCHES = '/tax/produce/batches'
export const API_TAX_PRODUCED_FORMS = '/tax/produce/forms'
export const API_TAX_PRODUCED_FORM = '/tax/produce/forms/:id'
// The events Forms 1099-A and 1099-C report, which have to be recorded before either can be run.
export const API_TAX_DEBT_EVENTS = '/tax/debt-events'
export const API_TAX_DEBT_EVENTS_META = '/tax/debt-events/meta'
export const API_TAX_DEBT_EVENT = '/tax/debt-events/:id'
export const API_TAX_DEBT_EVENT_EXCLUDE = '/tax/debt-events/:id/exclude'
export const API_TAX_RETURNS = '/tax/returns'
export const API_TAX_RETURNS_SCAN = '/tax/returns/scan'
export const API_TAX_RETURNS_SUMMARY = '/tax/returns/summary'
export const API_TAX_RETURN = '/tax/returns/:id'
export const API_TAX_RETURNS_REVIEW = '/tax/returns/review'
export const API_TAX_RETURNS_APPROVE = '/tax/returns/approve'
export const API_TAX_RETURNS_UNAPPROVE = '/tax/returns/unapprove'
export const API_TAX_RETURNS_VOID = '/tax/returns/void'
export const API_TAX_RETURN_CORRECT = '/tax/returns/:id/correct'
export const API_TAX_FILE_PREVIEW = '/tax/file/preview'
export const API_TAX_FILE = '/tax/file'
export const API_TAX_FILE_CONFIRM_UPLOAD = '/tax/file/:id/confirm-upload'
export const API_TAX_FILE_HISTORY = '/tax/file/history'
export const API_TAX_FILE_PAYLOAD = '/tax/file/:id/payload'
export const API_TAX_FILE_DOWNLOAD = '/tax/file/:id/download'
export const API_TAX_PRINT = '/tax/print'
export const API_TAX_STATEMENTS = '/tax/statements'
export const API_TAX_STATEMENTS_PUBLISH = '/tax/statements/publish'
export const API_TAX_STATEMENT_PDF = '/tax/statements/:recordId/pdf'
export const API_TAX_STATEMENT_TAKE_DOWN = '/tax/statements/:deliveryId/takeDown'
export const API_TAX_STATEMENT_CONSENT = '/tax/statements/consent'

export const API_SERVICING_STATEMENT_BATCH_DOWNLOAD = '/loan/servicing-statements/batches/download'
export const API_SERVICING_STATEMENT_BATCH_EMAIL = '/loan/servicing-statements/batches/email'
export const API_SERVICING_STATEMENT_DELETE = '/loan/servicing-statements/:id'
export const API_SERVICING_STATEMENT_BATCH_DELETE = '/loan/servicing-statements/batches/:id'

/**
 * HMDA
 */
export const API_LOAN_GET_HMDA = '/loan/hmda'
export const API_LOAN_GET_HMDA_BY_KEY = '/loan/hmda/:key'

/**
 * Loan Estimate (Page 1)
 */
export const API_LOAN_GET_LOAN_ESTIMATE = '/loan/loan-estimate'
export const API_LOAN_GET_LOAN_ESTIMATE_BY_KEY = '/loan/loan-estimate/:key'
export const API_LOAN_DOWNLOAD_LOAN_ESTIMATE_PAGE1 = '/loan/loan-estimate/download'

/**
 * MERS
 */
export const API_LOAN_GET_MERS = '/loan/mers'
export const API_LOAN_GET_MERS_BY_KEY = '/loan/mers/:key'

/**
 * Credit Denial
 */
export const API_LOAN_GET_CREDIT_DENIAL = '/loan/creditDenial'
export const API_LOAN_GET_CREDIT_DENIAL_BY_KEY = '/loan/creditDenial/:key'

export const API_LOAN_GET_ORDER_OUTS = '/loan/orderOuts'
export const API_LOAN_GET_ORDER_OUTS_DOWNLOAD = '/loan/orderOuts/download'
export const API_LOAN_GET_ORDER_OUTS_CHECK_PLACEHOLDER = '/loan/orderOuts/checkPlaceholder'
export const API_LOAN_SEND_ORDER_OUT_EMAIL = '/loan/orderOuts/sendEmail'

/**
 * Pool
 */
// Mortgage funds: setup, investors, ledger, valuations, distributions and payouts.
export const API_FUND = '/fund'
export const API_FUND_BY_ID = '/fund/:id'
export const API_FUND_HOLDINGS = '/fund/:id/holdings'
export const API_FUND_INVESTOR_DETAIL = '/fund/:id/investor/:positionId'
export const API_FUND_LEDGER = '/fund/:id/ledger'
export const API_FUND_STATEMENT = '/fund/:id/statement'
export const API_FUND_REPORT_CATALOG = '/fund/reports'
export const API_FUND_REPORT = '/fund/:id/report/:reportId'
export const API_FUND_REPORT_CSV = '/fund/:id/report/:reportId/csv'
export const API_FUND_RECONCILIATION = '/fund/:id/reconciliation'
export const API_FUND_COMPLIANCE = '/fund/:id/compliance'
export const API_FUND_ACTIVITY = '/fund/:id/activity'
export const API_FUND_AUDIT_PACK = '/fund/:id/auditPack'
export const API_FUND_AUDIT_PACK_CSV = '/fund/:id/auditPack/csv'
export const API_FUND_STATEMENT_EMAIL = '/fund/:id/statement/email'
export const API_FUND_SYNC = '/fund/:id/sync'
export const API_FUND_TRANSACTION = '/fund/:id/transaction'
export const API_FUND_TRANSACTION_BY_ID = '/fund/transaction/:transactionId'
export const API_FUND_ASSETS = '/fund/:id/assets'
export const API_FUND_ASSET_BY_ID = '/fund/asset/:assetId'
export const API_FUND_LIABILITIES = '/fund/:id/liabilities'
export const API_FUND_LIABILITY_BY_ID = '/fund/liability/:liabilityId'
export const API_FUND_INVESTORS = '/fund/:id/investors'
export const API_FUND_DIRECTORY = '/fund/investors'
export const API_FUND_DIRECTORY_BY_ID = '/fund/investors/:investorId'
export const API_FUND_DIRECTORY_ACTIVITY = '/fund/investors/:investorId/activity'
export const API_FUND_DIRECTORY_STATEMENT = '/fund/investors/:investorId/statement'
export const API_FUND_DIRECTORY_STATEMENT_EMAIL = '/fund/investors/:investorId/statement/email'
export const API_FUND_AVAILABLE_INVESTORS = '/fund/:id/investors/available'
export const API_FUND_COMBINED_QUEUE = '/fund/payouts/queue'
export const API_FUND_COMBINED_PAYOUT = '/fund/payouts'
export const API_FUND_INVESTOR_BY_ID = '/fund/investor/:investorId'
export const API_FUND_CONTRIBUTION = '/fund/:id/contribution'
export const API_FUND_REDEMPTION = '/fund/:id/redemption'
export const API_FUND_NAV = '/fund/:id/nav'
export const API_FUND_NAV_CALCULATE = '/fund/:id/nav/calculate'
export const API_FUND_NAV_BY_ID = '/fund/nav/:snapshotId'
export const API_FUND_NAV_FINALIZE = '/fund/nav/:snapshotId/finalize'
export const API_FUND_DISTRIBUTIONS = '/fund/:id/distributions'
export const API_FUND_DISTRIBUTION_PREVIEW = '/fund/:id/distributions/preview'
export const API_FUND_DISTRIBUTION_BY_ID = '/fund/distribution/:runId'
export const API_FUND_DISTRIBUTION_RECALCULATE = '/fund/distribution/:runId/recalculate'
export const API_FUND_DISTRIBUTION_APPROVE = '/fund/distribution/:runId/approve'
export const API_FUND_DISTRIBUTION_REVERSE = '/fund/distribution/:runId/reverse'
export const API_FUND_ALLOCATION_BY_ID = '/fund/allocation/:allocationId'
export const API_FUND_ALLOCATION_REPRINT = '/fund/allocation/:allocationId/reprint'
export const API_FUND_PAYOUT_QUEUE = '/fund/:id/payouts/queue'
export const API_FUND_PAYOUTS = '/fund/:id/payouts'
export const API_FUND_PAYOUT_BY_ID = '/fund/payout/:payoutRunId'
export const API_FUND_PAYOUT_NACHA = '/fund/payout/:payoutRunId/nacha'
export const API_FUND_PAYOUT_REVERSE = '/fund/payout/:payoutRunId/reverse'
export const API_FUND_PAYOUT_PROVIDER = '/fund/payout/provider'
export const API_FUND_PAYOUT_SUBMIT_PROVIDER = '/fund/payout/:payoutRunId/submitProvider'

// Custodians, certificates, transfers and tax reporting.
export const API_FUND_TRUSTEES = '/fund/trustee'
export const API_FUND_TRUSTEE_BY_ID = '/fund/trustee/:trusteeId'
export const API_FUND_CERTIFICATES = '/fund/:id/certificates'
export const API_FUND_CERTIFICATE = '/fund/:id/certificate'
export const API_FUND_CERTIFICATES_BY_POSITION = '/fund/certificate/position/:positionId'
export const API_FUND_CERTIFICATE_SURRENDER = '/fund/certificate/:certificateId/surrender'
export const API_FUND_EXCHANGES = '/fund/:id/exchanges'
export const API_FUND_EXCHANGE = '/fund/:id/exchange'
export const API_FUND_TRANSFERS = '/fund/:id/transfers'
export const API_FUND_TRANSFER = '/fund/:id/transfer'
export const API_FUND_TAX_YEAR = '/fund/:id/tax/:taxYear'
export const API_FUND_TAX_STATEMENTS = '/fund/:id/tax/:taxYear/statements'
export const API_FUND_TAX_CLOSE = '/fund/:id/tax/:taxYear/close'
export const API_FUND_CAPITAL_YEARS = '/fund/:id/capital-years'
export const API_FUND_CAPITAL_YEAR = '/fund/:id/capital-year'
export const API_FUND_RETURNS = '/fund/:id/returns'

export const API_LOAN_POOL = '/pool'
export const API_LOAN_POOL_BY_ID = '/pool/:id'
export const API_LOAN_POOL_BY_LOAN_ID = '/pool/:id/:loanID'

export const API_LOAN_DOWNLOAD_CONDITIONS = '/loan/downloadConditions'

/**
 * Penalty
 */
export const API_LOAN_PENALTY = '/loan/penalty'

export const API_BROKER_VERIFICATION_TEMPLATE_BY_TYPE = '/brokerVerification/:type'
export const API_BROKER_VERIFICATION_TEMPLATE_DOCUMENT = '/brokerVerification/document/:type'

/**
 * Loan Validation
 */
export const API_LOAN_VALIDATION = 'loan/validation'
export const API_LOAN_VALIDATION_LOG = 'loan/validation/log/:key/:type/:index'

/**
 * Analyze File
 */
export const API_LOAN_ANALYZE_DOCUMENT = '/loan/analyzeDocument'
export const API_LOAN_EXTRACT_DOCUMENT_BY_SECTIONS = `${API_LOAN_ANALYZE_DOCUMENT}/extractBySections`
export const API_LOAN_ANALYZE_DOCUMENT_HISTORY = `${API_LOAN_ANALYZE_DOCUMENT}/history`

/**
 * Mails
 */
export const API_LOAN_MAIL = '/loan/mail'
export const API_LOAN_MAIL_BY_ID = '/loan/mail/:id'
export const API_LOAN_MAIL_DOWNLOAD = '/loan/mail/downloadCsv'
export const API_LOAN_MAIL_LOG = '/loan/mail/log/:id/:key'

/**
 * Manage UTM
 */
export const API_UTM = '/admin/utm'
export const API_ANALYSIS = '/admin/utm/analysis'
export const API_UTM_DOWNLOAD = '/admin/utm/download'
export const API_UTM_ANALYSIS_DOWNLOAD = '/admin/utm/analysis/download'
export const API_UTM_ADD_TRACKING = '/api/utm/tracking/:pageName'

/**
 * Loan Options
 */
export const API_LOAN_OPTIONS = '/loan/loanOption'
export const API_LOAN_OPTION_BY_ID = '/loan/loanOption/:id'
export const API_LOAN_OPTION_REORDER = '/loan/loanOption/reorder'
export const API_LOAN_SEND_LOAN_OPTIONS_TO = '/loan/sendLoanOptionsTo'
export const API_LOAN_PROCEED_LOAN_OPTION = '/loan/proceedLoanOption'

/**
 * Email Logs
 */
export const API_EMAIL_LOGS = '/admin/emailLogs'
export const API_EMAIL_LOG_DETAILS = '/admin/emailLogs/:id'

/**
 * Calendar
 */
export const API_CALENDAR_EVENTS = '/calendar/events'

/**
 * AI
 */
export const API_AI_PROMPT = '/ai/prompt/:identifier/:type'
export const API_AI_ASSISTANT_PROMPT = '/ai/assistant/prompt'
export const API_AI_ASSISTANT_PROMPT_STREAM = '/ai/assistant/prompt/stream'
export const API_AI_ASSISTANT_PROMPT_STOP = '/ai/assistant/prompt/stop'
export const API_AI_ASSISTANT_MODEL = '/ai/assistant/model'
export const API_AI_ASSISTANT_HISTORY = '/ai/assistant/history'
export const API_AI_ASSISTANT_CONVERSATIONS = '/ai/assistant/conversations'
export const API_AI_ASSISTANT_UPLOAD = '/ai/assistant/upload'

// The borrower's own application assistant. Two sets of routes on purpose: `/apply/*` is public and
// reached with an opaque draft token (an applicant with no account yet), `/borrowers/assistant/*`
// needs a borrower session. They are separate doors because they have different reach - only the
// signed-in one can see a loan.
export const API_BORROWER_APPLY_SESSION = '/apply/session'
export const API_BORROWER_APPLY_SCHEMA = '/apply/schema'
export const API_BORROWER_APPLY_DRAFT = '/apply/draft'
export const API_BORROWER_APPLY_PROMPT_STREAM = '/apply/prompt/stream'
export const API_BORROWER_APPLY_UPLOAD = '/apply/upload'
export const API_BORROWER_APPLY_SUBMIT = '/apply/submit'
export const API_BORROWER_ASSISTANT_SCHEMA = '/borrowers/assistant/schema'
export const API_BORROWER_ASSISTANT_DRAFT = '/borrowers/assistant/draft'
export const API_BORROWER_ASSISTANT_PROMPT_STREAM = '/borrowers/assistant/prompt/stream'
export const API_BORROWER_ASSISTANT_UPLOAD = '/borrowers/assistant/upload'
export const API_BORROWER_ASSISTANT_SUBMIT = '/borrowers/assistant/submit'

export const API_AI_PROJECTS = '/ai/projects'
export const API_AI_PROJECT = '/ai/projects/:id'
export const API_AI_SHAREABLE_GROUPS = '/ai/shareable-groups'
export const API_AI_PROJECT_SHARES = '/ai/projects/:id/shares'
export const API_AI_PROJECT_SHARE = '/ai/projects/:id/shares/:shareId'
export const API_AI_PROJECT_KNOWLEDGE = '/ai/projects/:id/knowledge'
export const API_AI_PROJECT_KNOWLEDGE_UPLOAD = '/ai/projects/:id/knowledge/upload'
export const API_AI_PROJECT_KNOWLEDGE_ITEM = '/ai/projects/:id/knowledge/:knowledgeId'
export const API_AI_PROJECT_KNOWLEDGE_FILE = '/ai/projects/:id/knowledge/:knowledgeId/file'
export const API_AI_PROJECT_ARTIFACTS = '/ai/projects/:id/artifacts'
export const API_AI_PROJECT_ARTIFACT = '/ai/projects/:id/artifacts/:artifactId'
export const API_AI_PROJECT_CONVERSATIONS = '/ai/projects/:id/conversations'
export const API_AI_CONVERSATION = '/ai/conversations/:conversationId'
export const API_AI_CONVERSATION_MESSAGES = '/ai/conversations/:conversationId/messages'

export const API_AI_CONNECTORS = '/ai/connectors'
export const API_AI_CONNECTORS_CATALOG = '/ai/connectors/catalog'
export const API_AI_CONNECTOR = '/ai/connectors/:id'
export const API_AI_CONNECTOR_CONNECT = '/ai/connectors/:id/connect'
export const API_AI_CONNECTOR_AUTHORIZE = '/ai/connectors/:id/authorize'
export const API_AI_CONNECTOR_REDIRECT_URI = '/ai/connectors/redirect-uri'
export const API_AI_PROJECT_CONNECTORS = '/ai/projects/:id/connectors'

/**
 * Scheduled project runs. The schedule routes are not nested under the project, because a
 * schedule is edited and watched from either the project workspace or the loan it runs on.
 */
export const API_AI_PROJECT_SCHEDULES = '/ai/projects/:id/schedules'
export const API_AI_SCHEDULES = '/ai/schedules'
export const API_AI_SCHEDULE = '/ai/schedules/:scheduleId'
export const API_AI_SCHEDULE_RUNS = '/ai/schedules/:scheduleId/runs'
export const API_AI_SCHEDULE_RUN = '/ai/schedules/:scheduleId/run'
export const API_AI_SCHEDULE_PREVIEW_LOANS = '/ai/projects/:id/schedules/preview-loans'

/**
 * Slack / Teams integration channels
 */
export const API_API_KEYS = '/integrations/apiKey'
export const API_API_KEY_SCOPES = '/integrations/apiKey/scopes'
export const API_API_KEY_BY_ID = '/integrations/apiKey/:id'
export const API_API_KEY_REVOKE = '/integrations/apiKey/:id/revoke'
export const API_INTEGRATION_CHANNELS = '/integrations/channel'
export const API_INTEGRATION_CHANNEL_BY_ID = '/integrations/channel/:id'
export const API_INTEGRATION_CHANNEL_TEST = '/integrations/channel/:id/test'

/**
 * Borrowing Bases
 */
export const API_BORROWING_BASES = 'loan/borrowingBase'
export const API_BORROWING_BASES_ORDER = 'loan/borrowingBase/order'
export const API_BORROWING_BASE_BY_ID = 'loan/borrowingBase/:id'
export const API_BORROWING_BASE_PREVIEW = 'loan/borrowingBase/:id/preview'
export const API_BORROWING_BASE_SUMMARY = 'loan/borrowingBase/:id/summary'
export const API_BORROWING_BASE_DOWNLOAD = 'loan/borrowingBase/:id/download/:format'
export const API_BORROWING_BASE_ADVANCES = 'loan/borrowingBase/:id/advances'
export const API_BORROWING_BASE_ADVANCE_APPROVE = 'loan/borrowingBase/:id/advances/:advanceId/approve'
export const API_BORROWING_BASE_ADVANCE_DECLINE = 'loan/borrowingBase/:id/advances/:advanceId/decline'
export const API_BORROWING_BASE_ADVANCES_APPROVE_BULK = 'loan/borrowingBase/:id/advances/approve-bulk'
export const API_BORROWING_BASE_ADVANCES_DECLINE_BULK = 'loan/borrowingBase/:id/advances/decline-bulk'

/**
 * Billing
 */
export const API_BILLING = '/billing'
export const API_BILLING_PDF = '/billing/:id/pdf'

/**
 * Notifications (the header bell)
 */
export const API_NOTIFICATION = '/notification'
export const API_NOTIFICATION_UNREAD_COUNT = '/notification/unreadCount'
export const API_NOTIFICATION_TOAST = '/notification/toast'
export const API_NOTIFICATION_READ = '/notification/read'
export const API_NOTIFICATION_BY_ID = '/notification/:id'

/**
 * Print & Mail: the queue of physical letters, what they cost, and the runs that fill it.
 *
 * Queue-then-dispatch, so nothing here posts a letter inside the request. `POST .../pieces`
 * writes a row and returns; a cron tick submits it to the provider and USPS tracking arrives by
 * webhook. Every cost is an estimate from the tenant's rate card - the vendor returns no price on
 * create - and is labelled one wherever it is shown.
 */
export const API_PRINT_MAIL_CONFIG = '/print-mail/config'
export const API_PRINT_MAIL_STATUS = '/print-mail/status'
export const API_PRINT_MAIL_QUOTE = '/print-mail/quote'
export const API_PRINT_MAIL_VERIFY_ADDRESS = '/print-mail/verify-address'
export const API_PRINT_MAIL_PIECES = '/print-mail/pieces'
export const API_PRINT_MAIL_PIECE = '/print-mail/pieces/:id'
export const API_PRINT_MAIL_PIECE_DOCUMENT = '/print-mail/pieces/:id/document'
export const API_PRINT_MAIL_PIECE_CANCEL = '/print-mail/pieces/:id/cancel'
export const API_PRINT_MAIL_PIECE_RELEASE = '/print-mail/pieces/:id/release'
export const API_PRINT_MAIL_DISPATCH = '/print-mail/dispatch'
export const API_PRINT_MAIL_BATCHES = '/print-mail/batches'
export const API_PRINT_MAIL_BATCH = '/print-mail/batches/:id'
export const API_PRINT_MAIL_TAX_FORM_ELIGIBILITY = '/print-mail/tax-forms/eligibility'
export const API_PRINT_MAIL_TAX_FORM_RUN = '/print-mail/tax-forms/run'
